import { useState, useEffect, useRef, useCallback } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Search, Trash2, Send, Loader2, MoreVertical,
  FileDown, MessageCircle, Copy, UserPlus, GripVertical, Eye, Lock, Package, Undo2, X,
} from 'lucide-react';
import { Modal } from '../../components/Modal';
import { arredondarTotalDesconto, mascaraTelefoneBR, orcamentoTravadoParaEdicao, totalComDescontoPct } from '@fiodecobre/shared';
import { useAuth } from '../../lib/auth';
import { api, ApiError, getToken, MSG_EMERGENCIA } from '../../lib/api';
import { useEmergencia } from '../../lib/emergencia';
import { useListaTeclado } from '../../lib/useListaTeclado';
import { outraDescricaoQueCasou } from '../../lib/outras-descricoes';
import { useToast } from '../../components/Toast';
import { useAlertModal } from '../../components/AlertModalProvider';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { EstoqueEfetivo } from '../../components/EstoqueEfetivo';
import { ModalSenhaGerencial } from '../../components/orcamento/ModalSenhaGerencial';
import { ModalOverrideBase } from '../../components/orcamento/ModalOverrideBase';
import { ModalEnviarCaixa, type FormaPagamentoModal } from '../../components/orcamento/ModalEnviarCaixa';
import { ModalEnviarWhatsApp } from '../../components/orcamento/ModalEnviarWhatsApp';
import { ModalBuscarCliente } from '../../components/orcamento/ModalBuscarCliente';
import { ModalRegistrarDevolucaoVendaCm } from '../../components/orcamento/ModalRegistrarDevolucaoVendaCm';
import { ReconhecerProdutoCamera } from '../../components/reconhecimento/ReconhecerProdutoCamera';
import { FotoProduto } from '../../components/FotoProduto';
import { LightboxImagem } from '../atendimento/componentes/LightboxImagem';
import gerarPdfOrcamento from '../../utils/gerarPdfOrcamento';

const fmt = (v: number | string): string =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Subtotal da LINHA em 4 casas (bate com o Preco Unit., que tambem exibe 4). Os totais
// gerais (Bruto/Desconto/Liquido) seguem em 2 casas — sao o valor de dinheiro cobrado.
const fmt4 = (v: number | string): string =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

// Cor do aviso "preco atualizado do cadastro" (DA-124) na celula de Preco Unit. Era o mesmo
// #fde68a do realce padrao ate a DA-407 — quando a linha marcada tambem virou amarela, o aviso
// desaparecia dentro dela. Ambar convive com o amarelo do realce sem virar alarme (nao e vermelho).
const AMARELO_REAJUSTE = '#f59e0b';

interface ItemOrc {
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  unidade_sigla: string | null;
  marca_nome: string | null;
  permite_fracionado: boolean;
  estoque_atual: number | null;
  estoque_efetivo: number | null;  // proprio + o que o pai gera por fracionamento (DA-261)
  localizacao: string | null;      // prateleira do produto — so orienta o vendedor, nao vai pro PDF
  quantidade: number;
  preco_original: number;   // preco CHEIO da linha: nasce = preco cartao do cadastro, e vira o
                            // valor fixado a mao quando o vendedor sobe o preco (base_manual)
  // Migration 0258 — o preco cheio desta linha foi fixado A MAO, acima da tabela. Enquanto for
  // true o reajuste automatico nao encosta na linha (senao o acrescimo evapora na reabertura).
  base_manual: boolean;
  // Preco de cartao VIVO do cadastro. Guardado a parte porque `preco_original` deixa de ser o
  // espelho da tabela assim que a base e' fixada a mao — sem isto nao haveria pra onde voltar.
  // E' o mesmo numero que o reajuste automatico usaria, entao os dois nunca brigam.
  preco_tabela: number;
  preco_pix: number;        // preco pix de tabela — base do "Bruto" exibido no rodape
  preco_unitario: number;
  desconto_percentual: number;
  desconto_valor: number;
  subtotal: number;
  reajustado?: boolean;     // true quando o preco_original foi atualizado do cadastro neste carregamento
}

interface ProdutoSugestao {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  outras_descricoes?: string | null;
  foto_url: string | null;
  unidade_sigla: string | null;
  marca_nome: string | null;
  permite_fracionado: boolean;
  estoque_atual: number | null;
  estoque_efetivo: number | string | null;  // proprio + fracionamento do pai (DA-261)
  localizacao: string | null;
  preco_cartao: number | string;
  preco_pix: number | string;
  status?: string | null;
}

interface ClienteSugestao {
  id: number;
  nome: string;
  razao_social: string | null;
  tipo_pessoa: string | null;
  codigo_legado: string | null;
  cpf_cnpj: string | null;
  cidade: string | null;
  uf: string | null;
  consignacao?: boolean;
}

/** Contato do WhatsApp sem ficha de cliente (`GET /api/clientes/contatos-sem-cadastro`). */
interface ContatoSemCadastro {
  contato_id: number;
  nome: string;
  /** 10 ou 11 digitos, sem o 55. */
  numero: string;
  foto_url: string | null;
}

interface PagamentoEnvio {
  forma_pagamento_id: number;
  valor: number | string;
  dias_vencimento?: number;
  card_parcelas?: number;
  conta_cartao?: string; // 'mp' | 'pagbank' — maquininha escolhida no orcamento
}

interface OrcamentoCarregado {
  numero: string;
  // Vendedor de quem editou por ultimo + emissao real: o PDF le os dois daqui, nao do usuario
  // logado nem de `new Date()` (DA-397). O `GET /:id` ja devolvia ambos (`SELECT o.*` + JOIN).
  vendedor_nome?: string | null;
  vendedor_login?: string | null;
  criado_em?: string | null;
  status?: string | null;
  tipo?: string | null;
  remessa_emitida?: boolean | null;
  cliente_id: number | null;
  cliente_nome: string | null;
  cliente_codigo: string | null;
  cliente_consignacao?: boolean | null;
  /** Marca de "nao quis informar o telefone" (migration 0277). O `GET /:id` faz `SELECT o.*`. */
  telefone_recusado?: boolean | null;
  recusa_nome?: string | null;
  recusa_etiqueta_ids?: number[] | null;
  /** Contato que a venda capturou (migration 0281, [DA-697]) — vem pelo mesmo `SELECT o.*`. */
  contato_capturado_id?: number | null;
  // Programa do Prestador (DA-435): quem GANHA o credito, independente de quem paga.
  prestador_id?: number | null;
  prestador_nome?: string | null;
  tipo_nf: string | null;
  forma_pagamento_id: number | null;
  observacoes: string | null;
  comprador: string | null;
  senha_gerencial_usada: boolean;
  override_base: number | string | null;
  override_base_auto: number | string | null;
  itens: Array<{
    produto_id: number;
    codigo_interno: string | null;
    produto_descricao: string;
    foto_url: string | null;
    unidade_sigla: string | null;
    marca_nome: string | null;
    permite_fracionado: boolean;
    estoque_atual: number | null;
    estoque_efetivo?: number | string | null;
    localizacao?: string | null;
    quantidade: number | string;
    preco_original: number | string;
    base_manual?: boolean;
    preco_cartao_catalogo?: number | string;
    preco_pix?: number | string;
    preco_unitario: number | string;
    desconto_percentual: number | string;
    desconto_valor: number | string;
    subtotal: number | string;
    reajustado?: boolean;
  }>;
}

/** Largura da lista de sugestoes da busca de produto (px CSS) — independente do campo. */
const LARGURA_LISTA_PRODUTOS = 760;

export function OrcamentoNovo() {
  const toast = useToast();
  const { alerta } = useAlertModal();
  // MODO EMERGENCIA (lib/emergencia.ts): o orcamento grava no aparelho; o que precisa do servidor desabilita.
  const emergencia = useEmergencia();
  const { confirmar } = useDialog();
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams<{ id?: string }>();
  const isEditMode = !!editId && editId !== 'novo';

  // Quem abriu daqui diz pra onde o voltar leva (mesma forma da CompraInformativa/CotacaoRespostas).
  // Sem isso a seta joga sempre na lista de Orcamentos, e quem veio da Gestao de Vendas perde a fila
  // em que estava. O state gruda no history, entao sobrevive ao F5 desta tela — que e' o que se quer.
  const voltarPara = (location.state as { from?: string } | null)?.from ?? '/orcamentos';
  // State que a tela de origem precisa pra reabrir onde estava (o /caixa escolhe a aba por state).
  const voltarState = (location.state as { fromState?: unknown } | null)?.fromState;

  const buscaProdutoRef = useRef<HTMLInputElement>(null);
  const ultimaBuscaRef = useRef('');
  const qtdRefs = useRef<Record<number, HTMLInputElement | null>>({});
  const itemRefs = useRef<Record<number, HTMLTableRowElement | null>>({});
  // Foto do item ampliada (pedido dele: o vendedor identifica o produto pela foto). O lightbox
  // e' o MESMO do chat, reusado como na Gestao de Vendas ([DA-701]).
  const [fotoAmpliada, setFotoAmpliada] = useState<{ url: string; legenda: string } | null>(null);
  const [qtdInputs, setQtdInputs] = useState<Record<number, string>>({});
  // Mesmo buffer da Qtd, pelo mesmo motivo: enquanto o campo esta sendo digitado ele guarda o
  // TEXTO CRU. Reformatar a cada tecla (o `toFixed(4)` que estava aqui) reescreve o value pelo
  // React e joga o cursor pro fim — digitar o 2o algarismo virava impossivel.
  const [precoInputs, setPrecoInputs] = useState<Record<number, string>>({});

  // Dados do orcamento
  const [clienteId, setClienteId] = useState<number>(1);
  const [clienteNome, setClienteNome] = useState<string>('Consumidor');
  const [clienteCodigo, setClienteCodigo] = useState<string | null>(null);
  // "Nao quis informar o Telefone" (migration 0277). Registro da RECUSA — nao esconde a linha do
  // acompanhamento, s'o diz por que ela esta sem numero.
  const [telefoneRecusado, setTelefoneRecusado] = useState(false);
  // Nome e etiquetas de quem recusou ([DA-780], migration 0307) — sem os dois a recusa nao libera o
  // envio. ⛔ NAO entram no autosave: quem grava e' a rota `registrar-recusa`, num gesto so'.
  const [recusaNome, setRecusaNome] = useState<string | null>(null);
  const [recusaEtiquetaIds, setRecusaEtiquetaIds] = useState<number[]>([]);
  // Contato que esta venda capturou (migration 0281, [DA-697]) — uma das tres saidas da trava de
  // identificacao do Enviar ao Caixa. ⛔ NAO entra no payload do autosave: a gravacao dele e' da
  // rota `capturar-contato`, que tambem cria o contato. Mandar daqui abriria uma segunda mao no
  // mesmo campo, e a mais lenta venceria.
  const [contatoCapturadoId, setContatoCapturadoId] = useState<number | null>(null);
  // O state `clienteConsignacao` saiu na DA-415: o unico consumidor dele era o item "Venda CM" do
  // menu, que mudou de tela (agora e o 3o botao do modal de finalizar, no caixa). Quem le o selo
  // hoje e o caixa, por `PedidoCaixa.cliente_consignacao`.
  const [tipoDoc, setTipoDoc] = useState<'venda' | 'remessa'>('venda');
  // Acerto da consignacao (DA-373): LEGADO. A criacao (montar-acerto) e a emissao 5114 foram
  // REMOVIDAS (o contador vetou a consignacao mercantil); sobrou so a EXIBICAO dos 2 acertos ja
  // cancelados no banco — abrem como venda read-only. Nenhum acerto novo nasce mais.
  const [ehAcerto, setEhAcerto] = useState(false);
  // Venda CM (DA-381): orcamento liberado sem NF-e — estoque ja baixado, somente leitura.
  const [ehVendaCm, setEhVendaCm] = useState(false);
  // Levado pelo cliente com credito da loja em Trocas e Devolucoes (migration 0340) — so muda a frase da faixa.
  const [levadoComCredito, setLevadoComCredito] = useState(false);
  const [modalDevolucaoVendaCm, setModalDevolucaoVendaCm] = useState(false);
  const [formaPagamentoId, setFormaPagamentoId] = useState<number | null>(null);
  const [itens, setItens] = useState<ItemOrc[]>([]);
  const [observacoes, setObservacoes] = useState('');
  const [comprador, setComprador] = useState('');
  // Programa do Prestador (DA-435) — DESACOPLA quem paga de quem ganha: a venda pode sair no nome
  // do cliente final, do prestador ou de "Consumidor", e o credito segue SEMPRE este campo.
  // Qualquer vendedora preenche; a lista so oferece quem tem o selo (a adesao e' com o dono).
  const [prestadorId, setPrestadorId] = useState<number | null>(null);
  const [prestadorNome, setPrestadorNome] = useState('');
  const [buscaPrestador, setBuscaPrestador] = useState('');
  const [prestadoresSugestao, setPrestadoresSugestao] = useState<ClienteSugestao[]>([]);
  const [showPrestadorSugestoes, setShowPrestadorSugestoes] = useState(false);
  // "O operador quer ver a lista agora" — focar/clicar/digitar liga, escolher/sair desliga. Sem isto
  // a lista so existia pra quem ja soubesse o nome de um parceiro e digitasse 2 letras as cegas.
  const [prestadorAberto, setPrestadorAberto] = useState(false);
  const reqPrestadorRef = useRef(0);
  const [descontoTotalPerc, setDescontoTotalPerc] = useState(0);
  const [descontoMode, setDescontoMode] = useState<'valor' | 'perc'>('perc');
  const [descontoInput, setDescontoInput] = useState('');
  const [acrescimoMode, setAcrescimoMode] = useState<'valor' | 'perc'>('perc');
  const [acrescimoInput, setAcrescimoInput] = useState('');
  // A vendedora APAGOU o que estava no campo (onChange de cheio pra vazio) — so isso, ou digitar 0,
  // conta como "tirar o acrescimo". Campo vazio por si so nao: ele nasce vazio ao reabrir e e'
  // limpo por codigo depois de um desconto, e um foco/desfoque ali nao pode apagar preco nenhum.
  const acrescimoApagadoRef = useRef(false);
  const [senhaGerencialUsada, setSenhaGerencialUsada] = useState(false);
  // Autorizacao gerencial ja gravada no orcamento (independe do perfil que reabre).
  const [senhaGerencialPersistida, setSenhaGerencialPersistida] = useState(false);
  const [editandoTotal, setEditandoTotal] = useState(false);
  const [totalOverrideInput, setTotalOverrideInput] = useState('');
  // Override de base manual (Ctrl+Shift+X, 0082) — uso raro, so admin/gerente. Valor fixado a
  // mao que vira a base da venda (venda+nota+parcelamento); comissao deriva sozinha (DA-169).
  const [overrideBase, setOverrideBase] = useState<number | null>(null);
  const [overrideAuto, setOverrideAuto] = useState<number | null>(null);
  const [showModalOverride, setShowModalOverride] = useState(false);
  const totalInputRef = useRef<HTMLInputElement>(null);
  const [orcamentoNumero, setOrcamentoNumero] = useState('');
  // Vendedor e data de EMISSAO do orcamento carregado, pro PDF (DA-397). Antes o `montarDadosPdf`
  // usava o usuario LOGADO e `new Date()`, entao reimprimir o orcamento de outra pessoa saia com o
  // nome de quem imprimiu e datado de hoje. O `vendedor_nome` acompanha quem editou por ultimo
  // (o `PUT /orcamentos/:id` regrava `vendedor_id` a cada salvamento — regra que ja existia).
  // Vazio = orcamento novo, ainda nao salvo: ai o logado E o vendedor e o fallback acerta.
  const [orcVendedorNome, setOrcVendedorNome] = useState('');
  const [orcCriadoEm, setOrcCriadoEm] = useState('');
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);

  // Auto-save
  const [savedId, setSavedId] = useState<number | null>(isEditMode ? Number(editId) : null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>(isEditMode ? 'saved' : 'idle');
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readyForAutoSave = useRef(!isEditMode);
  // Orcamento que ja virou venda finalizada e travado: e a fonte da nota fiscal, editar
  // desalinha a nota. Corrigir = reabrir a venda no Caixa. O ref bloqueia o autosave/normalizacao
  // de forma sincrona (sem esperar re-render); o state so pinta o aviso.
  const edicaoBloqueadaRef = useRef(false);
  const [bloqueadoEdicao, setBloqueadoEdicao] = useState(false);
  // Distingue o MOTIVO da trava pra o aviso: remessa em consignacao ja emitida (DA-371) x venda
  // finalizada. Muda so o texto do banner; a trava (autosave/handlers/backend) e a mesma.
  const [remessaTravadaEdicao, setRemessaTravadaEdicao] = useState(false);
  // Edicao concorrente (DA-406): nome de quem esta com a caneta. Quinto motivo de trava — a unica
  // que nao vem do ESTADO do documento, e sim de outra pessoa estar dentro dele agora.
  const [edicaoDeOutro, setEdicaoDeOutro] = useState<string | null>(null);
  // Segura a caneta? So quem segura renova (a cada 30s) e devolve ao sair — um leitor que soltasse
  // a trava ao fechar a aba derrubaria a de quem esta editando de verdade.
  const canetaMinhaRef = useRef(false);
  // Envio ao caixa em andamento: o enviar-caixa e uma transacao pesada (calibracao + baixa de
  // estoque). Sem trava/sinal, a operadora clicava, nao via nada e clicava de novo. O ref bloqueia
  // a reentrada de forma sincrona (sem esperar re-render); o state desabilita o botao + mostra
  // "Enviando..." no modal.
  const enviandoRef = useRef(false);
  const [enviando, setEnviando] = useState(false);

  // Menu popup PDF/WhatsApp
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Drag-and-drop reordenar itens
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  // Linha marcada no clique (padrao de grid da casa: amarelo #fde68a + letra preta). Guarda o
  // `produto_id`, NAO o indice: o item pode ser arrastado pra outra posicao, e marca por posicao
  // escorrega calada pra linha vizinha. `produto_id` e' unico por linha (adicionar repetido soma
  // na quantidade, nao cria uma 2a linha). So realce — nao decide nada, entao vive em useState.
  const [linhaMarcada, setLinhaMarcada] = useState<number | null>(null);

  // Busca de produto
  const [buscaProduto, setBuscaProduto] = useState('');
  const [produtosSugestao, setProdutosSugestao] = useState<ProdutoSugestao[]>([]);
  const [showSugestoes, setShowSugestoes] = useState(false);
  const [sugestaoIdx, setSugestaoIdx] = useState(-1);
  const sugestoesRef = useRef<HTMLDivElement>(null);
  const [sugestoesRect, setSugestoesRect] = useState<{ top: number; left: number; width: number } | null>(null);

  // Busca de cliente
  const [buscaCliente, setBuscaCliente] = useState('');
  const [clientesSugestao, setClientesSugestao] = useState<ClienteSugestao[]>([]);
  const [showClienteSugestoes, setShowClienteSugestoes] = useState(false);
  const [contatosSugestao, setContatosSugestao] = useState<ContatoSemCadastro[]>([]);
  const [cadastrandoContato, setCadastrandoContato] = useState<number | null>(null);
  const [showModalListaClientes, setShowModalListaClientes] = useState(false);

  // Tipo de NF
  const [tipoNF, setTipoNF] = useState<'nfce' | 'nfe'>('nfce');
  const [, setNfeValidacao] = useState<{ valido: boolean; faltam: string[] }>({ valido: true, faltam: [] });
  const [, setNfeValidando] = useState(false);

  // Auxiliares
  const [formasPagamento, setFormasPagamento] = useState<FormaPagamentoModal[]>([]);

  // Modais
  const [showModalSenha, setShowModalSenha] = useState(false);
  const [senhaCallback, setSenhaCallback] = useState<(() => void) | null>(null);
  const [showModalEnviar, setShowModalEnviar] = useState(false);
  const [showModalWhatsApp, setShowModalWhatsApp] = useState(false);
  // Caixa "Fly" ao lado do WhatsApp (07/10/2026): marcada, o WhatsApp manda o fly com o preco do
  // orcamento no lugar do PDF. `flyPronto` e' a arte ja desenhada que o modal mostra e envia.
  const [comFly, setComFly] = useState(false);
  const [gerandoFly, setGerandoFly] = useState(false);
  const [flyPronto, setFlyPronto] = useState<{ imagem_base64: string; legenda: string; filename: string } | null>(null);
  const [showModalVendaMinima, setShowModalVendaMinima] = useState(false);

  // Carregar formas de pagamento
  useEffect(() => {
    api.get<FormaPagamentoModal[]>('/api/orcamentos/aux/formas-pagamento').then((res) => {
      setFormasPagamento(res);
      if (!isEditMode) {
        const pix = res.find((f) => f.tipo === 'pix');
        setFormaPagamentoId(pix ? pix.id : res[0]?.id ?? null);
      }
    });
  }, [isEditMode]);

  // Orcamento vive sempre no PIX: cartao e decisao de CAIXA, nao de orcamento (DA-168 revisado).
  // Um orcamento antigo salvo no cartao volta pro pix ao abrir (uma vez so) — a vendedora cota e
  // desconta no pix; se a venda for no cartao, o fechamento repoe a margem sozinho (DA-076/DA-274).
  // So no load: depois disso a vendedora ainda pode trocar a forma a mao (mostra preco cheio; a
  // troca zera o desconto via handleFormaPagamento).
  const formaNormalizadaRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || formaNormalizadaRef.current) return;
    if (edicaoBloqueadaRef.current) return; // orcamento travado (venda finalizada): nao mexe na forma
    if (formasPagamento.length === 0 || formaPagamentoId == null) return;
    formaNormalizadaRef.current = true;
    const fp = formasPagamento.find((f) => f.id === formaPagamentoId);
    if (fp && (fp.tipo === 'cartao_credito' || fp.tipo === 'cartao_debito')) {
      const pix = formasPagamento.find((f) => f.tipo === 'pix');
      if (pix) setFormaPagamentoId(pix.id);
    }
  }, [isEditMode, formasPagamento, formaPagamentoId]);

  // Foca o campo de busca de produto ao abrir a tela
  useEffect(() => {
    const t = setTimeout(() => buscaProdutoRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  // --- Caneta da edicao (DA-406) -------------------------------------------------------------
  // Quem chega primeiro edita; os demais ficam em somente leitura. Admin/gerente pode tomar a
  // caneta respondendo a pergunta na abertura. Devolve `true` se saiu daqui podendo editar.
  async function reivindicarCaneta(orcId: number): Promise<boolean> {
    type Resp = { ok: boolean; por_nome?: string };
    let r: Resp;
    try {
      r = await api.post<Resp>(`/api/orcamentos/${orcId}/editando`);
    } catch {
      // Rede/servidor fora: nao trancar a tela por causa disso. O PUT ainda tem a trava real, entao
      // no maximo o save falha — melhor do que impedir de trabalhar por um erro de infra.
      canetaMinhaRef.current = true;
      return true;
    }
    if (r.ok) {
      canetaMinhaRef.current = true;
      return true;
    }

    const dono = r.por_nome || 'outro usuario';
    if (isAdmin) {
      const assumo = await confirmar(
        `Editado por ${dono}. Deseja editar assim mesmo? A tela dele passa para somente leitura.`,
        { titulo: 'Orcamento em edicao' },
      );
      if (assumo) {
        try {
          const forcado = await api.post<Resp>(`/api/orcamentos/${orcId}/editando`, { assumir: true });
          if (forcado.ok) {
            canetaMinhaRef.current = true;
            return true;
          }
        } catch { /* cai pra somente leitura abaixo */ }
      }
      // Admin que recusou ja foi avisado pela propria pergunta — nao repetir em popup.
      entrarEmSomenteLeitura(dono);
      return false;
    }
    entrarEmSomenteLeitura(dono);
    // A tarja sozinha nao para o operador: ele chega com o cliente na frente, vai direto pro campo
    // e so entende o que houve depois de tentar. O popup obriga o "OK" antes de mexer. Ele NAO
    // substitui a tarja — o popup se fecha e some, a tarja continua respondendo "por que travou?".
    await alerta(
      `Este orcamento esta sendo editado por ${dono} agora. Voce pode consultar, mas nao alterar. Quando ${dono} sair, atualize a tela (F5) para editar.`,
      { titulo: 'Somente leitura', tipo: 'erro' },
    );
    return false;
  }

  // Vira a tela em somente leitura por causa de outra pessoa. Mexe no REF primeiro (sincrono) —
  // esperar o re-render deixaria o autosave passar no meio do caminho.
  function entrarEmSomenteLeitura(dono: string) {
    canetaMinhaRef.current = false;
    readyForAutoSave.current = false;
    edicaoBloqueadaRef.current = true;
    setBloqueadoEdicao(true);
    setEdicaoDeOutro(dono);
  }

  // Renova a caneta enquanto a aba esta aberta, e devolve ao sair. Sem a renovacao, a trava
  // expiraria em 2 min com o vendedor ainda digitando; sem a devolucao, o colega esperaria esses
  // 2 min a toa depois que ele fechou a tela.
  useEffect(() => {
    // Vale pro salvo E pro recem-criado (o `savedId` aparece no 1o autosave). Quem NAO tem a caneta
    // nunca bate nem solta — o `canetaMinhaRef` e' o portao, e e' o que impede um leitor de tomar a
    // trava no primeiro tique ou de derrubar a de quem esta editando ao fechar a aba.
    if (!savedId) return;
    const bater = () => {
      if (!canetaMinhaRef.current) return;
      api.post(`/api/orcamentos/${savedId}/editando`).catch(() => {});
    };
    const tick = setInterval(bater, 30_000);
    const soltar = () => {
      if (!canetaMinhaRef.current) return;
      canetaMinhaRef.current = false;
      // `keepalive` faz a requisicao sobreviver ao fechamento da aba — sem ele o navegador aborta
      // o fetch no unload e a caneta so seria liberada quando expirasse.
      fetch(`/api/orcamentos/${savedId}/editando`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener('beforeunload', soltar);
    return () => {
      clearInterval(tick);
      window.removeEventListener('beforeunload', soltar);
      soltar();
    };
  }, [savedId]);

  // Carregar dados em edicao
  useEffect(() => {
    if (!isEditMode) return;
    api
      .get<OrcamentoCarregado>(`/api/orcamentos/${editId}`)
      .then(async (orc) => {
        // Consignacao Fase 2 (DA-376): remessa com NF-e autorizada tem tela DEDICADA read-only —
        // desvia pra la em vez de renderizar esta tela auto-travada. Cobre link direto e navegacao
        // antiga (a aba Remessas ja manda direto pra rota nova). A Fase 1 (bloqueadoEdicao abaixo)
        // fica de rede de seguranca caso o desvio nao ocorra.
        if (orc.tipo === 'remessa' && orc.remessa_emitida === true) {
          navigate(`/orcamentos/${editId}/remessa`, { replace: true });
          return;
        }
        // Trava de edicao (Caminho B — o orcamento e a fonte da nota ao vivo). Dois casos:
        // (1) venda ja finalizada/cancelada; (2) remessa em consignacao ja emitida (DA-371) — a
        // NF-e de remessa saiu deste orcamento e o saldo do retorno/acerto + o estoque dependem
        // dele. Remessa cancelada NAO trava (remessa_emitida ja exclui status!='autorizada').
        const remessaTravada = orc.tipo === 'remessa' && orc.remessa_emitida === true;
        // Acerto (DA-373): documento DERIVADO (soma dos saldos das remessas, preco travado) —
        // editar na mao desalinharia a venda 5114 das remessas que ela referencia. Somente leitura,
        // mas o "Enviar ao Caixa" segue vivo (o acerto PRECISA finalizar pra virar a venda do mes) —
        // por isso o botao de envio checa `!ehAcerto`, ao contrario dos demais casos travados.
        const acertoTravado = orc.tipo === 'acerto';
        // Venda CM (DA-381): estoque ja baixado, somente leitura. Devolver material tem porta
        // propria — o botao "Registrar devolucao" no banner (nao reabre a edicao dos itens).
        const vendaCmTravada = orc.tipo === 'venda_cm';
        const travado = orcamentoTravadoParaEdicao(orc.status) || remessaTravada || acertoTravado || vendaCmTravada;
        edicaoBloqueadaRef.current = travado;
        setBloqueadoEdicao(travado);
        setRemessaTravadaEdicao(remessaTravada);
        setEhVendaCm(vendaCmTravada);
        setLevadoComCredito(orc.status === 'compra_credito');
        setOrcamentoNumero(orc.numero);
        // O PDF imprime o LOGIN do vendedor (pedido dele, 02/10/2026), nao o nome do cadastro.
        setOrcVendedorNome(orc.vendedor_login || orc.vendedor_nome || '');
        setOrcCriadoEm(orc.criado_em || '');
        setClienteId(orc.cliente_id || 1);
        setClienteNome(orc.cliente_nome || 'Consumidor');
        setClienteCodigo(orc.cliente_codigo || null);
        setTelefoneRecusado(!!orc.telefone_recusado);
        setRecusaNome(orc.recusa_nome ?? null);
        setRecusaEtiquetaIds(Array.isArray(orc.recusa_etiqueta_ids) ? orc.recusa_etiqueta_ids : []);
        // Se o cliente e' Consumidor, contato capturado de um ciclo anterior nao identifica ninguem —
        // inicializa como null pra que o proximo autosave limpe o banco (evita tela pulada no reload).
        const cidCarregado = orc.cliente_id || 1;
        setContatoCapturadoId(cidCarregado === 1 ? null : (orc.contato_capturado_id ?? null));
        setTipoDoc(orc.tipo === 'remessa' ? 'remessa' : 'venda');
        setEhAcerto(orc.tipo === 'acerto');
        setBuscaCliente(orc.cliente_nome && orc.cliente_nome !== 'Consumidor' ? orc.cliente_nome : '');
        setTipoNF((orc.tipo_nf as 'nfce' | 'nfe') || 'nfce');
        setFormaPagamentoId(orc.forma_pagamento_id);
        setObservacoes(orc.observacoes || '');
        setComprador(orc.comprador || '');
        setPrestadorId(orc.prestador_id ?? null);
        setPrestadorNome(orc.prestador_nome || '');
        setBuscaPrestador(orc.prestador_nome || '');

        const itensCarregados: ItemOrc[] = (orc.itens || []).map((item) => ({
          produto_id: item.produto_id,
          codigo_interno: item.codigo_interno,
          descricao: item.produto_descricao,
          foto_url: item.foto_url,
          unidade_sigla: item.unidade_sigla,
          marca_nome: item.marca_nome,
          permite_fracionado: !!item.permite_fracionado,
          estoque_atual: item.estoque_atual != null ? Number(item.estoque_atual) : null,
          estoque_efetivo: item.estoque_efetivo != null ? Number(item.estoque_efetivo) : null,
          localizacao: item.localizacao ?? null,
          quantidade: Number(item.quantidade),
          preco_original: Number(item.preco_original),
          base_manual: !!item.base_manual,
          preco_tabela: Number(item.preco_cartao_catalogo ?? item.preco_original),
          // Orcamento salvo nao guarda o preco pix base — usa o preco_unitario gravado
          // como referencia (sem desconto fantasma ao reabrir). Ver project_orcamento_sempre_pix.
          preco_pix: Number(item.preco_pix ?? item.preco_unitario),
          preco_unitario: Number(item.preco_unitario),
          desconto_percentual: Number(item.desconto_percentual) || 0,
          desconto_valor: Number(item.desconto_valor) || 0,
          subtotal: Number(item.subtotal),
          reajustado: !!item.reajustado,
        }));
        setItens(itensCarregados);
        const qtdReajustados = itensCarregados.filter((it) => it.reajustado).length;
        if (qtdReajustados > 0) {
          toast.mostrar(
            `Preco de ${qtdReajustados} produto${qtdReajustados > 1 ? 's' : ''} atualizado do cadastro`,
            'aviso',
          );
        }

        // Mesma base do rodape: cada linha ao centavo antes de somar (senao a soma crua x a soma
        // das linhas ja arredondadas divergiria por sub-centavo e acusaria um desconto inexistente).
        const totalOrig = itensCarregados.reduce((acc, it) => acc + Math.round(it.preco_original * it.quantidade * 100), 0) / 100;
        const totalAtual = itensCarregados.reduce((acc, it) => acc + Math.round(it.subtotal * 100), 0) / 100;
        if (totalOrig > 0 && totalAtual < totalOrig) {
          const descRealPerc = Math.round(((totalOrig - totalAtual) / totalOrig) * 10000) / 100;
          setDescontoTotalPerc(descRealPerc);
          setDescontoInput(String(descRealPerc));
          setDescontoMode('perc');
        } else {
          setDescontoTotalPerc(0);
        }

        // `usada` = autorizei NESTA sessao (admin se autoriza sozinho) -> serve pra NAO reperguntar
        // a senha a cada mexida. `persistida` = o pedido ja chegou autorizado do banco -> serve pra
        // deixar passar, mas a vendedora que MUDAR o desconto e' perguntada de novo (o gerente
        // autorizou 6%, nao 30%). A dupla e' de proposito; quem grava e' o `carimboGerencial`. DA-405.
        const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
        setSenhaGerencialUsada(ehAdmin ? !!orc.senha_gerencial_usada : false);
        setSenhaGerencialPersistida(!!orc.senha_gerencial_usada);

        // Override de base manual (0082): restaura badge + mantem persistido ao reabrir.
        setOverrideBase(orc.override_base != null ? Number(orc.override_base) : null);
        setOverrideAuto(orc.override_base_auto != null ? Number(orc.override_base_auto) : null);

        setLoadingEdit(false);
        // Orcamento travado (venda finalizada) nunca liga o autosave — edicao nao persiste.
        if (travado) return;
        // A caneta (DA-406) e' pedida ANTES de o autosave ligar. Se outra pessoa esta com o pedido,
        // esta tela nasce em somente leitura e nunca chega a gravar por cima — ligar o autosave e
        // perguntar depois deixaria uma janela gravando em cima do trabalho do colega.
        const minha = await reivindicarCaneta(Number(editId));
        if (!minha) return;
        setTimeout(() => { readyForAutoSave.current = true; }, 300);
      })
      .catch(() => navigate('/orcamentos'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, isEditMode]);

  // Auto-save debounced
  const doAutoSave = useCallback(
    async (currentItens: ItemOrc[], currentSavedId: number | null) => {
      if (currentItens.length === 0) return;
      setSaveStatus('saving');
      try {
        const itensPayload = currentItens.map((item) => ({
          produto_id: item.produto_id,
          quantidade: item.quantidade,
          preco_original: item.preco_original,
          base_manual: item.base_manual,
          preco_unitario: item.preco_unitario,
          desconto_percentual: item.desconto_percentual,
          desconto_valor: item.desconto_valor,
          subtotal: item.subtotal,
        }));
        const payload = {
          cliente_id: clienteId,
          tipo: ehAcerto ? 'acerto' : tipoDoc,
          cliente_nome_custom: clienteId === 1 && clienteNome !== 'Consumidor' ? clienteNome : null,
          telefone_recusado: telefoneRecusado,
          contato_capturado_id: contatoCapturadoId,
          forma_pagamento_id: formaPagamentoId,
          desconto_total_percentual: 0,
          // NUNCA mandar so o `usada`: o PUT faz COALESCE e `false` nao e' null, entao um false
          // APAGARIA a autorizacao ja gravada. Era o que acontecia quando a vendedora reabria um
          // pedido autorizado — o autosave zerava o carimbo do gerente sozinho (DA-405).
          senha_gerencial_usada: senhaGerencialUsada || senhaGerencialPersistida,
          observacoes,
          comprador: comprador || null,
          prestador_id: prestadorId,
          tipo_nf: tipoNF,
          status: 'salvo',
          override_base: overrideBase,
          override_base_auto: overrideAuto,
          itens: itensPayload,
        };
        if (currentSavedId) {
          await api.put(`/api/orcamentos/${currentSavedId}`, payload);
          // O PUT regrava `vendedor_id` com quem salvou, entao o nome do PDF acompanha na hora —
          // sem isso o state local ficaria com o vendedor antigo ate um F5 (DA-397).
          if (usuario?.login || usuario?.nome) setOrcVendedorNome(usuario.login || usuario.nome);
        } else {
          const res = await api.post<{ id: number; numero: string }>('/api/orcamentos', payload);
          setSavedId(res.id);
          setOrcamentoNumero(res.numero);
          window.history.replaceState(null, '', `/orcamentos/${res.id}`);
          // Orcamento que ACABOU de nascer tambem precisa da caneta (DA-406): sem isto ele ficaria
          // com `editando_por` nulo e um colega que abrisse pela lista pegaria a caneta por cima de
          // quem esta montando o carrinho. Ninguem pode ter tomado ainda — o id nasceu agora.
          canetaMinhaRef.current = true;
          api.post(`/api/orcamentos/${res.id}/editando`).catch(() => {});
        }
        setSaveStatus('saved');
      } catch (err) {
        setSaveStatus('error');
        // Alguem assumiu a edicao por baixo (DA-406). O autosave e' o detector: em ate 1,5s a tela
        // vira somente leitura e AVISA, em vez de deixar o vendedor digitando num rascunho que
        // ninguem mais vai gravar. Sem isto ele so descobriria a perda no F5, com o trabalho fora.
        const corpo = err instanceof ApiError ? (err.corpo as { codigo?: string; por_nome?: string }) : null;
        if (corpo?.codigo === 'edicao_de_outro') {
          const dono = corpo.por_nome || 'outro usuario';
          entrarEmSomenteLeitura(dono);
          alerta(
            `${dono} assumiu a edicao deste orcamento. Sua tela passou para somente leitura e o que voce alterou agora nao foi salvo. Atualize a tela (F5) para ver a versao atual.`,
            { titulo: 'Edicao assumida', tipo: 'erro' },
          );
        }
      }
    },
    // tipoDoc/ehAcerto SAO lidos no payload (tipo) — sem eles nas deps a funcao congela
    // o tipo: trocar Venda<->Remessa nao dispara save (o efeito depende de doAutoSave) e,
    // pior, um save por outro campo mandaria o tipo velho, rebaixando remessa/acerto pra venda.
    //
    // ⛔ `telefoneRecusado` entrou em 09/09/2026 ([DA-697]) por ESTE mesmo motivo, e a falta dele
    // deixou o recurso da [DA-686] sem funcionar por um dia: o efeito abaixo dispara ao marcar a
    // caixa (ele tem a marca nas deps dele), mas chamava esta funcao MEMOIZADA com o valor
    // velho, entao o payload saia `telefone_recusado: false` e a marca nunca chegava ao banco.
    // Medido: zero marcacoes em 57 orcamentos, que eu quase li como "ninguem usa".
    // ⚠️ A licao e' de leitura: campo lido no payload TEM que estar nas deps — o efeito que
    // dispara e a funcao que grava sao duas listas diferentes, e so a segunda decide o valor.
    [clienteId, clienteNome, formaPagamentoId, observacoes, comprador, prestadorId, senhaGerencialUsada, senhaGerencialPersistida, tipoNF, overrideBase, overrideAuto, tipoDoc, ehAcerto, telefoneRecusado],
  );

  const pendingSave = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (!readyForAutoSave.current) return;
    if (itens.length === 0) return;
    setSaveStatus('saving');
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    pendingSave.current = () => doAutoSave(itens, savedId);
    autoSaveTimer.current = setTimeout(() => {
      pendingSave.current = null;
      doAutoSave(itens, savedId);
    }, 1500);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
    // `telefoneRecusado` entra nas dependencias porque marcar a caixa E' a unica acao daquele
    // controle: sem ela o autosave nao dispararia e a marca s'o iria pro banco de carona no
    // proximo item mexido — ou nunca, num orcamento ja montado, que e' justamente o caso comum.
  }, [itens, clienteId, clienteNome, formaPagamentoId, observacoes, comprador, prestadorId, telefoneRecusado, doAutoSave, savedId]);

  function flushAndNavigate(path: string, state?: unknown) {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    if (pendingSave.current) {
      void pendingSave.current();
      pendingSave.current = null;
    }
    navigate(path, state ? { state } : undefined);
  }

  /**
   * Grava AGORA o que o autosave ainda deve, e ESPERA — ao contrario do `flushAndNavigate`, que
   * dispara e sai (ali a tela vai embora de todo jeito).
   *
   * ⛔ Existe por causa da corrida com o envio ao caixa ([DA-697]): o autosave tem 1,5s de espera
   * e o `enviar-caixa` NAO regrava o pedido, so' le o que esta no banco. Escolher o cliente (ou
   * marcar "nao quis informar") e clicar em Enviar antes disso mandava a venda com o dado ANTIGO
   * — hoje a venda ia como Consumidor, calada; com a trava de identificacao no ar, a recusa seria
   * pior: o servidor barraria o envio dizendo pra informar o cliente que ela **acabou de**
   * informar, e o balcao ficaria sem saida com o cliente na frente.
   */
  async function gravarPendencias() {
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    const pendente = pendingSave.current;
    if (!pendente) return;
    pendingSave.current = null;
    await pendente();
  }

  // Buscar produtos
  useEffect(() => {
    if (buscaProduto.length < 2) {
      setProdutosSugestao([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        // somente_vendaveis=true exclui os itens de Uso/Consumo (consumo proprio da
        // loja, nao pra revenda) — a vendedora nunca ve/vende inseticida no orcamento.
        // Este e o unico ponto de busca de produto do fluxo de venda: o caixa apenas
        // finaliza pedidos ja montados aqui, nao busca produto.
        const res = await api.get<ProdutoSugestao[]>('/api/produtos', { query: { q: buscaProduto, somente_vendaveis: true, com_inativos: '1' } });
        setProdutosSugestao(res);
        setSugestaoIdx(-1);
        setShowSugestoes(true);
      } catch { /* ignora */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [buscaProduto]);

  // Posicao do dropdown de sugestoes (fixed) + click outside
  useEffect(() => {
    if (!showSugestoes) return;
    const atualizar = () => {
      const r = buscaProdutoRef.current?.getBoundingClientRect();
      // A LISTA e' mais larga que o campo (pedido dele, 08/10/2026, com print marcando ~760px na
      // janela dele): o campo encolheu pra dar espaco ao Cliente, e a lista nao precisa encolher
      // junto. Nunca menor que o campo, e nunca passando da borda direita da janela.
      if (r) setSugestoesRect({
        top: r.bottom + 4,
        left: r.left,
        width: Math.max(r.width, Math.min(LARGURA_LISTA_PRODUTOS, window.innerWidth - r.left - 16)),
      });
    };
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (sugestoesRef.current?.contains(t)) return;
      if (buscaProdutoRef.current?.contains(t)) return;
      setShowSugestoes(false);
    };
    atualizar();
    window.addEventListener('resize', atualizar);
    window.addEventListener('scroll', atualizar, true);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('resize', atualizar);
      window.removeEventListener('scroll', atualizar, true);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSugestoes]);

  // Buscar clientes
  useEffect(() => {
    const buscaTrim = buscaCliente.trim();
    // Compara os DOIS em maiuscula (DA-409). O campo so vira maiusculo quando alguem DIGITA nele
    // (`toUpperCase` no onChange) — quem preenche por codigo (load do orcamento, escolha na
    // sugestao, modal de clientes) grava o nome como esta no cadastro, "Matusalem Eficiencia".
    // Comparando so um lado, isso nunca batia com o nome ja em maiuscula: a tela abria buscando o
    // cliente que ela mesma acabou de por, e o combobox nascia aberto por cima da lista de itens.
    // O CSS de maiuscula global escondia a diferenca — os dois textos PARECIAM iguais na tela.
    const isClienteAtual = buscaTrim && buscaTrim.toUpperCase() === (clienteNome || '').trim().toUpperCase();
    if (buscaTrim.length < 2 || isClienteAtual) {
      setClientesSugestao([]);
      setContatosSugestao([]);
      setShowClienteSugestoes(false);
      return;
    }
    let cancelado = false;
    const timer = setTimeout(async () => {
      // As duas listas saem juntas: quem ainda nao tem ficha mas ja falou com a loja no WhatsApp
      // aparece embaixo dos clientes, pra cadastrar num clique (relato dele em 15/10/2026 — o
      // ALEANDRO existia so' como contato, e o campo respondia "nada"). A segunda consulta falhar
      // nao derruba a primeira.
      const [res, contatos] = await Promise.all([
        api.get<ClienteSugestao[]>('/api/clientes', { query: { busca: buscaTrim } }).catch(() => null),
        api.get<ContatoSemCadastro[]>('/api/clientes/contatos-sem-cadastro', { query: { busca: buscaTrim } }).catch(() => []),
      ]);
      if (cancelado || !res) return;
      setClientesSugestao(res);
      setContatosSugestao(contatos);
      setShowClienteSugestoes(true);
    }, 300);
    return () => { cancelado = true; clearTimeout(timer); };
  }, [buscaCliente, clienteNome]);

  // Busca do parceiro (DA-435) — so oferece quem tem o SELO. Filtrar aqui, e nao mostrar todo
  // mundo e recusar depois, e' o que faz "escolher entre os marcados" ser a operacao da vendedora:
  // ela nao precisa saber quem aderiu, a lista ja sabe.
  //
  // CLICAR ja abre a lista INTEIRA (termo vazio = todos os marcados). Os parceiros sao poucos por
  // construcao — a adesao e' combinada um a um com o dono —, entao exigir 2 letras escondia o unico
  // recurso do campo: quem nao sabe QUEM aderiu nao descobre digitando as cegas. Com termo, filtra.
  useEffect(() => {
    if (!prestadorAberto) {
      setShowPrestadorSugestoes(false);
      return;
    }
    const t = buscaPrestador.trim();
    // Texto identico ao ja escolhido nao e' busca — e' o valor em tela. Lista todos, pra trocar.
    const termo = t.length >= 2 && t.toUpperCase() !== (prestadorNome || '').trim().toUpperCase() ? t : '';
    const timer = setTimeout(async () => {
      // A lista completa sai na hora e a filtrada tem 300ms de debounce: sem o guarda, a resposta
      // mais LENTA sobrescreveria a mais nova (a corrida do CLAUDE.md).
      const req = ++reqPrestadorRef.current;
      try {
        const res = await api.get<ClienteSugestao[]>('/api/clientes', {
          query: { busca: termo, programa_prestador: 'true' },
        });
        if (req !== reqPrestadorRef.current) return;
        setPrestadoresSugestao(res);
        setShowPrestadorSugestoes(true);
      } catch { /* ignora */ }
    }, termo ? 300 : 0);
    return () => clearTimeout(timer);
  }, [buscaPrestador, prestadorNome, prestadorAberto]);

  function selecionarPrestador(c: ClienteSugestao) {
    if (edicaoBloqueadaRef.current) return; // DA-408 — campo que muda dinheiro nao mexe em doc travado
    setPrestadorId(c.id);
    setPrestadorNome(c.nome);
    setBuscaPrestador(c.nome);
    setPrestadorAberto(false); // escolheu: a lista fecha e NAO reabre sozinha (o input segue focado)
    setShowPrestadorSugestoes(false);
  }

  function limparPrestador() {
    if (edicaoBloqueadaRef.current) return;
    setPrestadorId(null);
    setPrestadorNome('');
    setBuscaPrestador('');
    setPrestadorAberto(false);
    setShowPrestadorSugestoes(false);
  }

  const tecladoPrestador = useListaTeclado(prestadoresSugestao, selecionarPrestador);

  // Consignacao (DA-371): centraliza o efeito de trocar de cliente — liga/desliga o toggle e, quando
  // o novo cliente NAO tem selo, forca o documento de volta pra venda (senao um modo remessa antigo
  // ficaria preso num cliente que nao pode consignar). Consumidor/texto livre nunca tem selo.
  function aplicarSeloConsignacao(temSelo: boolean) {
    if (!temSelo) setTipoDoc('venda');
  }

  // Volta o pedido pra Consumidor. Apagar o campo e sair dele ja fazia isso, mas nada na tela
  // mostrava o caminho — ele escolheu o cliente errado e "nao conseguiu remover" (30/10/2026).
  // O X do campo chama esta mesma funcao, como o X do Parceiro.
  function limparCliente() {
    if (edicaoBloqueadaRef.current) return; // DA-408 — ver confirmarCliente
    setClienteId(1);
    setClienteNome('Consumidor');
    setClienteCodigo(null);
    setBuscaCliente('');
    // Voltou pra Consumidor: trava de identificacao deve voltar a pedir quem e' o comprador.
    setContatoCapturadoId(null);
    setTelefoneRecusado(false);
    aplicarSeloConsignacao(false);
    setShowClienteSugestoes(false);
  }

  function confirmarCliente() {
    // Trocar o cliente num documento travado nao era so inerte: o PDF monta o nome a partir DESTE
    // state, entao dava pra imprimir o pedido de um colega com outro cliente no cabecalho (DA-408).
    if (edicaoBloqueadaRef.current) return;
    const txt = buscaCliente.trim();
    const atual = (clienteNome || '').trim().toUpperCase();
    if (txt === atual) { setShowClienteSugestoes(false); return; }
    if (!txt) { limparCliente(); return; }
    if (clienteId !== 1) {
      setBuscaCliente(clienteNome);
      setShowClienteSugestoes(false);
      return;
    }
    setClienteNome(txt.toUpperCase());
    setClienteId(1);
    setClienteCodigo(null);
    aplicarSeloConsignacao(false);
    setShowClienteSugestoes(false);
  }

  function selecionarClienteSugestao(c: ClienteSugestao) {
    if (edicaoBloqueadaRef.current) return; // DA-408 — ver confirmarCliente
    setClienteId(c.id);
    setClienteNome(c.nome);
    setClienteCodigo(c.codigo_legado || null);
    aplicarSeloConsignacao(c.consignacao === true);
    setBuscaCliente(c.nome);
    setShowClienteSugestoes(false);
    setNfeValidacao({ valido: true, faltam: [] });
    if (tipoNF === 'nfe') selecionarTipoNF('nfe', c.id);
  }

  const tecladoCliente = useListaTeclado(clientesSugestao, selecionarClienteSugestao);

  // Contato do WhatsApp sem ficha → cria a ficha (nome + WhatsApp) e ja a usa no orcamento. E' o
  // mesmo `POST /api/clientes` do botao "Cadastrar" do modal de busca, entao vale a mesma trava de
  // duplicado: se o numero ja tem ficha, o 409 devolve qual e ela vira a escolhida.
  // ⚠️ So' por CLIQUE no botao da linha, nunca por Enter: o teclado da lista anda so' pelos
  // clientes, porque aqui o gesto GRAVA um cadastro.
  async function cadastrarContatoComoCliente(c: ContatoSemCadastro) {
    if (edicaoBloqueadaRef.current || cadastrandoContato) return;
    setCadastrandoContato(c.contato_id);
    try {
      const novo = await api.post<ClienteSugestao>('/api/clientes', { nome: c.nome, whatsapp: c.numero });
      toast.sucesso(`Cliente "${novo.nome}" cadastrado`);
      selecionarClienteSugestao(novo);
    } catch (e: any) {
      const corpo = e instanceof ApiError ? (e.corpo as { erro?: string; cliente_existente?: ClienteSugestao }) : null;
      if (corpo?.erro === 'whatsapp_duplicado' && corpo.cliente_existente) {
        toast.sucesso(`Este WhatsApp ja tinha cadastro: ${corpo.cliente_existente.nome}`);
        selecionarClienteSugestao(corpo.cliente_existente);
      } else {
        toast.erro(e?.message ?? 'Falha ao cadastrar o cliente');
      }
    } finally {
      setCadastrandoContato(null);
    }
  }

  async function adicionarProduto(produto: ProdutoSugestao) {
    if (edicaoBloqueadaRef.current) return; // travado (venda finalizada / remessa emitida)
    if (produtoInativo(produto)) { void alerta(MSG_PRODUTO_INATIVO); return; }
    clearOverride();
    const jaExiste = itens.find((i) => i.produto_id === produto.id);
    // Mede pelo estoque EFETIVO (proprio + fracionamento do pai): o enviar-caixa auto-fraciona
    // o pai quando o filho falta, entao so alertamos quando nem o pai cobre (DA-261).
    const estoqueEfetivo =
      produto.estoque_efetivo != null
        ? Number(produto.estoque_efetivo)
        : produto.estoque_atual != null
          ? Number(produto.estoque_atual)
          : null;
    const novaQtd = jaExiste ? jaExiste.quantidade + 1 : 1;
    if (estoqueEfetivo != null && estoqueEfetivo < novaQtd) {
      // O texto antigo prometia que o pedido "nao podera ser enviado ao caixa" — falso desde a
      // [DA-329]/[DA-337] (18/07/2026), quando a trava virou confirmacao. Aviso que descreve um
      // comportamento inexistente e pior que aviso nenhum ([DA-336]): a vendedora lia isso e
      // desistia da venda com o cliente na frente.
      //
      // O `await` e obrigatorio, nao estetico: o `alert()` nativo que estava aqui PARAVA o JS,
      // entao o item so entrava depois do OK e o setTimeout(50) do fim da funcao punha o foco no
      // campo Qtd. O modal da casa devolve promise — sem esperar, o foco seria roubado para tras
      // do overlay ([DA-638]).
      await alerta(
        `${produto.descricao}\nEstoque: ${estoqueEfetivo}  ·  Pedido: ${novaQtd}\n\n` +
          'Voce pode seguir normalmente: o item entra no orcamento e a venda NAO fica travada. ' +
          'Ao enviar ao caixa, o sistema vai pedir uma confirmacao.',
        { titulo: 'Estoque insuficiente', tipo: 'aviso' },
      );
    }
    let targetIdx: number;
    if (jaExiste) {
      targetIdx = itens.findIndex((i) => i.produto_id === produto.id);
      setItens(
        itens.map((i) =>
          i.produto_id === produto.id
            ? { ...i, quantidade: i.quantidade + 1, subtotal: (i.quantidade + 1) * i.preco_unitario }
            : i,
        ),
      );
    } else {
      targetIdx = itens.length;
      // O orcamento NASCE no preco cartao (cheio); o desconto que a vendedora
      // aplica (ate 5%) leva o preco ate o pix. Ver decisoes-arquitetura.
      const precoOrig = Number(produto.preco_cartao);
      const precoPix = Number(produto.preco_pix || produto.preco_cartao);
      let precoUnit = precoOrig;
      let descPerc = 0;
      let descVal = 0;
      if (itens.length > 0) {
        const ref = itens[0]!;
        if (ref.preco_unitario < ref.preco_original) {
          descPerc = Math.round(((ref.preco_original - ref.preco_unitario) / ref.preco_original) * 10000) / 100;
          descVal = Math.round(precoOrig * (descPerc / 100) * 100) / 100;
          precoUnit = Math.round((precoOrig - descVal) * 100) / 100;
        } else if (ref.preco_unitario > ref.preco_original) {
          const acrescPerc = ((ref.preco_unitario - ref.preco_original) / ref.preco_original) * 100;
          const acrescVal = Math.round(precoOrig * (acrescPerc / 100) * 100) / 100;
          precoUnit = Math.round((precoOrig + acrescVal) * 100) / 100;
        }
      }
      setItens([
        ...itens,
        {
          produto_id: produto.id,
          codigo_interno: produto.codigo_interno,
          descricao: produto.descricao,
          foto_url: produto.foto_url,
          unidade_sigla: produto.unidade_sigla,
          marca_nome: produto.marca_nome,
          permite_fracionado: !!produto.permite_fracionado,
          estoque_atual: produto.estoque_atual != null ? Number(produto.estoque_atual) : null,
          estoque_efetivo: produto.estoque_efetivo != null ? Number(produto.estoque_efetivo) : null,
          localizacao: produto.localizacao ?? null,
          quantidade: 1,
          preco_original: precoOrig,
          base_manual: false, // item novo nasce ancorado na tabela
          preco_tabela: precoOrig,
          preco_pix: precoPix,
          preco_unitario: precoUnit,
          desconto_percentual: descPerc,
          desconto_valor: descVal,
          subtotal: precoUnit,
        },
      ]);
    }
    ultimaBuscaRef.current = buscaProduto;
    setBuscaProduto('');
    setShowSugestoes(false);
    setSugestaoIdx(-1);
    setTimeout(() => {
      const el = qtdRefs.current[targetIdx];
      if (el) { el.focus(); el.select(); }
    }, 50);
  }

  function removerItem(idx: number) {
    if (edicaoBloqueadaRef.current) return; // travado (venda finalizada / remessa emitida)
    clearOverride();
    setItens(itens.filter((_, i) => i !== idx));
  }

  function dropItem(fromIdx: number | null, toIdx: number | null) {
    if (edicaoBloqueadaRef.current) return; // travado (venda finalizada / remessa emitida)
    if (fromIdx == null || toIdx == null || fromIdx === toIdx) return;
    const novos = [...itens];
    const [movido] = novos.splice(fromIdx, 1);
    if (!movido) return;
    const destino = fromIdx < toIdx ? toIdx - 1 : toIdx;
    novos.splice(destino, 0, movido);
    setItens(novos);
  }

  // Unidades que SEMPRE aceitam decimal mesmo se o produto nao tem `permite_fracionado` setado.
  // Cobre cabo por metro, peso, volume, area, comprimento (caso comum: cadastro legado sem o flag).
  const UNIDADES_FRACIONAVEIS = new Set(['MT', 'M', 'M2', 'M3', 'KG', 'G', 'L', 'ML', 'CM']);

  function atualizarQuantidade(idx: number, qtd: number | string) {
    if (edicaoBloqueadaRef.current) return; // travado (venda finalizada / remessa emitida)
    const raw = parseFloat(String(qtd).replace(',', '.'));
    if (isNaN(raw) || raw <= 0) return;
    clearOverride();
    let valor = raw;
    const item = itens[idx]!;
    const fracionavelPorUnidade = item.unidade_sigla && UNIDADES_FRACIONAVEIS.has(item.unidade_sigla.toUpperCase());
    if (!item.permite_fracionado && !fracionavelPorUnidade) {
      valor = Math.round(valor);
      if (valor <= 0) valor = 1;
    }
    setItens(
      itens.map((it, i) => {
        if (i !== idx) return it;
        return { ...it, quantidade: valor, subtotal: valor * it.preco_unitario };
      }),
    );
  }

  function onChangeQtdInput(idx: number, raw: string) {
    let s = raw.replace(/[^\d.,]/g, '').replace(/\./g, ',');
    const i = s.indexOf(',');
    if (i >= 0) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/,/g, '');
    setQtdInputs({ ...qtdInputs, [idx]: s });
    const num = parseFloat(s.replace(',', '.'));
    if (!isNaN(num) && num > 0) atualizarQuantidade(idx, s);
  }

  function onBlurQtdInput(idx: number) {
    const raw = qtdInputs[idx];
    if (raw == null) return;
    const num = parseFloat(String(raw).replace(',', '.'));
    if (!isNaN(num) && num > 0) atualizarQuantidade(idx, raw);
    const novo = { ...qtdInputs };
    delete novo[idx];
    setQtdInputs(novo);
  }


  function atualizarPreco(idx: number, novoPreco: number | string) {
    if (edicaoBloqueadaRef.current) return; // travado (venda finalizada / remessa emitida)
    const valor = parseFloat(String(novoPreco).replace(',', '.')) || 0;
    // Orcamento sempre tratado como pix durante a edicao — sem bloqueio por isCartao
    // (ver project_orcamento_sempre_pix). Cartao e validado so no envio ao caixa.
    const podeAbaixar = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
    if (podeAbaixar && !senhaGerencialUsada) {
      const item = itens[idx]!;
      if (valor < item.preco_original) {
        const novoSub = item.quantidade * valor;
        const simTotal = itens.reduce((acc, it, i) => (i === idx ? acc + novoSub : acc + it.subtotal), 0);
        const simDescPerc = totalOriginal > 0 ? ((totalOriginal - simTotal) / totalOriginal) * 100 : 0;
        // Limite de 5% de desconto pra vendedora; acima exige autorizacao gerencial.
        if (simDescPerc > 5.1) {
          pedirSenhaGerencial(() => atualizarPrecoInterno(idx, valor));
          return;
        }
      }
    }
    atualizarPrecoInterno(idx, valor);
  }

  function onChangePrecoInput(idx: number, raw: string) {
    let s = raw.replace(/[^\d.,]/g, '').replace(/\./g, ',');
    const i = s.indexOf(',');
    if (i >= 0) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/,/g, '');
    setPrecoInputs({ ...precoInputs, [idx]: s });
  }

  // O preco so e' COMMITADO na saida do campo (convencao da casa pra dinheiro: digita cru,
  // formata no blur). Commitar por tecla faria o `pedirSenhaGerencial` abrir no 1o algarismo,
  // quando o valor parcial ainda representa um desconto de 99%.
  function onBlurPrecoInput(idx: number) {
    const raw = precoInputs[idx];
    if (raw != null) {
      const novo = { ...precoInputs };
      delete novo[idx];
      setPrecoInputs(novo);
    }
    if (raw == null || raw === '') return;
    const num = parseFloat(raw.replace(',', '.'));
    if (isNaN(num) || num < 0) return; // texto invalido: o campo volta ao valor gravado
    // ZERO nao e' preco: e' o pedido de VOLTAR PRA TABELA (ideia dele, 28/08/2026). E' a unica
    // saida de uma linha com base fixada a mao, que de resto nao teria como desfazer.
    // ⚠️ Tem que vir ANTES do resto: zero cai como "desconto de 100%" no `atualizarPreco` e
    // abriria a senha gerencial pra um gesto que nao e' desconto nenhum.
    // Medido antes de escolher a sentinela: em toda a base ha 2 itens com preco 0, NENHUM em
    // venda finalizada — zero nunca foi preco de verdade aqui (regra da DA-608: sentinela nao
    // pode ser um valor legitimo do campo).
    if (num === 0) {
      restaurarPrecoTabela(idx);
      return;
    }
    const item = itens[idx]!;
    // Vendedora nao abaixa do preco de tabela — o valor digitado abaixo dele nao vale.
    const podeAbaixar = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
    if (!podeAbaixar && num < Number(item.preco_original)) {
      atualizarPreco(idx, Number(item.preco_original));
      return;
    }
    atualizarPreco(idx, num);
  }

  // Desfaz a base fixada a mao E qualquer desconto da linha, devolvendo o preco de cartao VIVO do
  // cadastro (o mesmo que o reajuste automatico usaria — os dois nunca discordam). Disparado por
  // digitar 0 no Preco Unit.
  function restaurarPrecoTabela(idx: number) {
    if (edicaoBloqueadaRef.current) return; // DA-408 — venda finalizada / remessa emitida
    const item = itens[idx]!;
    const tabela = Number(item.preco_tabela);
    // Produto sem preco de cartao no cadastro: nao ha tabela pra onde voltar. Dizer isso e' melhor
    // que zerar a linha calado — e o campo volta sozinho ao valor gravado, porque nao mexemos nele.
    if (!(tabela > 0)) {
      toast.erro('Este produto nao tem preco de tabela no cadastro — nada pra restaurar.');
      return;
    }
    clearOverride();
    setDescontoTotalPerc(0);
    setDescontoInput('');
    setAcrescimoInput('');
    setItens(
      itens.map((it, i) =>
        i !== idx
          ? it
          : {
              ...it,
              preco_original: tabela,
              base_manual: false,
              preco_unitario: tabela,
              desconto_percentual: 0,
              desconto_valor: 0,
              subtotal: Math.round(it.quantidade * tabela * 100) / 100,
            },
      ),
    );
    // O gesto e' invisivel — quem digita 0 espera zero. O aviso e' o que o ENSINA na 1a vez, e
    // nomear o valor deixa o operador conferir se voltou pro numero que ele esperava.
    toast.sucesso(`Preco de tabela restaurado: R$ ${fmt4(tabela)}`);
  }

  function atualizarPrecoInterno(idx: number, valor: number) {
    clearOverride();
    setDescontoTotalPerc(0);
    setDescontoInput('');
    setItens(
      itens.map((item, i) => {
        if (i !== idx) return item;
        // Preco ACIMA do cheio atual = o vendedor esta fixando um preco cheio novo (nao dando um
        // "desconto negativo"): a base sobe junto e a linha fica marcada como manual. E' isso que
        // faz o Bruto acompanhar — e ele fica ESTAVEL, porque desconto so mexe no preco_unitario.
        // ⛔ Nao tentar derivar a base de `preco_unitario` na hora de exibir: foi a 1a tentativa
        // (commit 0d18fc4e) e o desconto derrubava a propria base que deveria medir.
        const subiuABase = valor > item.preco_original;
        const base = subiuABase ? valor : item.preco_original;
        const descPerc = valor < base ? ((base - valor) / base) * 100 : 0;
        return {
          ...item,
          preco_original: base,
          base_manual: item.base_manual || subiuABase,
          preco_unitario: valor,
          desconto_percentual: Math.round(descPerc * 100) / 100,
          desconto_valor: valor < base ? base - valor : 0,
          subtotal: item.quantidade * valor,
        };
      }),
    );
  }

  function handleFormaPagamento(fpId: number) {
    setFormaPagamentoId(fpId);
    const fp = formasPagamento.find((f) => f.id === fpId);
    if (fp && (fp.tipo === 'cartao_debito' || fp.tipo === 'cartao_credito')) {
      setDescontoTotalPerc(0);
      setItens((prev) =>
        prev.map((item) => ({
          ...item,
          preco_unitario: item.preco_original,
          desconto_percentual: 0,
          desconto_valor: 0,
          subtotal: item.quantidade * item.preco_original,
        })),
      );
    }
  }

  // Calculos — "Bruto" = preco CARTAO (preco cheio). O orcamento nasce em cartao;
  // o desconto que a vendedora aplica (ate 5%) leva o preco ate o pix.
  // Cada LINHA arredonda ao centavo ANTES de somar (acumula em centavos inteiros) — mesma base
  // que o backend grava e que a NF usa. Somar os precos "cheios" de 4 casas e arredondar so no
  // fim fazia o Bruto (soma crua) destoar do Liquido/NF (soma das linhas ja arredondadas) em 1
  // centavo sem desconto nenhum. Agora Bruto e Liquido partem da MESMA base = nunca divergem a toa.
  // FONTE UNICA do "Bruto". Toda conta de desconto/acrescimo tem que fechar NESTA base — nunca
  // na soma crua de 4 casas. Eram duas definicoes: o rodape somava as linhas arredondadas (12,90)
  // e as funcoes de rateio somavam os precos crus (12,8930). Digitar 13,00 no total calculava o
  // acrescimo contra 12,90 (+0,10) mas fechava o alvo em 12,8930+0,10 = 12,99 — e a correcao de
  // centavo, mirando nesse 12,99, via diferenca zero e nao corrigia. O total travava em 12,99 pra
  // sempre. Pro lado do desconto o mesmo desencontro derrubava 1 centavo do valor digitado.
  // ⛔ NAO clampar o Bruto com `max(preco_original, preco_unitario)` — tentado e REVERTIDO em
  // 28/08/2026, no ar por 12 minutos. A formula e' a mesma que o caixa usa
  // (`dividirPagamentoCartaoNosItens`, `cheio = max(subtotal_bruto, total)`), mas LA os dois lados
  // sao snapshots parados (bruto do backend, total gravado do pedido) e AQUI o `totalOriginal` e
  // recalculado a cada render a partir dos itens VIVOS. Resultado: o desconto derruba o
  // `preco_unitario`, que era a propria base do Bruto clampado — a base desabava junto e comia o
  // desconto. Medido: preco 250 (Bruto 500), desconto de R$ 40 -> preco 230, Bruto volta pra
  // 463,10 e o rodape passa a declarar R$ 3,10 de desconto no lugar dos R$ 40 concedidos; tirar o
  // desconto depois nao devolvia os 250, porque ninguem guardava esse numero.
  // Pra o Bruto acompanhar um acrescimo ele precisa ser LEMBRADO (campo proprio, persistido),
  // nunca derivado do preco que o desconto mexe. Ver pendencias.md.
  const brutoLinha = (it: { preco_original: number; quantidade: number }) =>
    Math.round(it.preco_original * it.quantidade * 100) / 100;
  const brutoLista = (lista: { preco_original: number; quantidade: number }[]) =>
    lista.reduce((acc, it) => acc + Math.round(it.preco_original * it.quantidade * 100), 0) / 100;
  // O teto no preco Pix (DA-788) foi DESFEITO no mesmo dia, a pedido dele: o total deixava de ser
  // redondo (ORC 006911 saiu R$ 359,30). Volta a regra das 3 faixas sem teto.
  const totalOriginal = brutoLista(itens);
  const subtotal = itens.reduce((acc, item) => acc + Math.round(item.subtotal * 100), 0) / 100;

  // O orcamento mostra o total REDONDO durante a montagem (decisao do Ruan, 2026-06-26). O centavo da
  // calibracao de Pix (DA-233) NAO nasce mais aqui: so e aplicado quando o Pix e INFORMADO como forma
  // de pagamento — no enviar-caixa (forma pix declarada, orcamentos.routes) e na geracao do QR
  // (/cobrar cobre_total, pix-cobranca.routes). Assim venda em dinheiro/cartao/crediario fica redonda
  // de ponta a ponta (nao da pra cobrar nem trocar 1 centavo no balcao); so o Pix, que precisa de
  // valor unico pra casar sozinho, quebra o centavo — e ai aparece quebrado no QR/cupom que o cliente
  // ve. Ver project_calibracao_centavos_pix e feedback_valor_ajustado_visivel_no_documento.
  const total = subtotal;
  // Desconto REAL aplicado pela vendedora (base do limite de 5% / senha gerencial): sempre sobre o
  // subtotal CRU.
  const descontoRealValor = totalOriginal - subtotal;
  const descontoTotalValor = Math.max(0, totalOriginal - total);

  // Subtotais EXIBIDOS por item = subtotais crus (sem calibracao no orcamento; ver bloco acima).
  const subtotaisExibidos: number[] = itens.map((it) => Math.round(it.subtotal * 100) / 100);

  // Desconto acumulado em % sobre o preco cartao (mesma base do desconto digitado). So pra EXIBIR.
  function calcDescontoAcumulado(): number {
    if (totalOriginal === 0) return 0;
    return (descontoRealValor / totalOriginal) * 100;
  }

  // Trava dos 5% CIENTE do arredondamento. O % cru (calcDescontoAcumulado) engana em item
  // barato: um 5% limpo em R$ 5,27 vira R$ 5,00 (arredonda pro multiplo de 5 centavos) e o cru
  // le 5,12% — falso estouro do teto. O desconto so passa de 5% de verdade quando o total
  // realizado fica ABAIXO do que um 5% limpo produz apos O MESMO arredondamento. Assim o gate
  // bate com o handleAplicarDesconto (que ja libera 5% digitado). Ver desconto.ts / DA-233.
  function descontoAcimaDoLimite(): boolean {
    if (totalOriginal <= 0) return false;
    const pisoCincoPorCento = totalComDescontoPct(totalOriginal, 5);
    return total < pisoCincoPorCento - 0.005;
  }

  const isAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';

  function pedirSenhaGerencial(callback: () => void) {
    if (isAdmin) {
      setSenhaGerencialUsada(true);
      callback();
      return;
    }
    setSenhaCallback(() => callback);
    setShowModalSenha(true);
  }

  function handleAplicarDesconto() {
    // O `disabled` do campo e' a tranca visivel; este ref e' a de verdade (sincrona, e cobre o
    // onBlur que dispara quando a trava chega COM o campo ja focado — caso do usuario que perde a
    // caneta digitando). Ate a DA-408 desconto/acrescimo/total ficavam editaveis num documento
    // travado, "inertes" porque o autosave estava desligado: o total mudava na tela do operador e
    // nada era gravado. Em documento morto passava; com outra pessoa editando, e' mentira na cara.
    if (edicaoBloqueadaRef.current) return;
    const val = parseFloat(descontoInput) || 0;
    if (val === 0) {
      aplicarDescontoTotalInterno(0);
      return;
    }
    let percEfetivo: number;
    let valorDireto: number | null = null;
    if (descontoMode === 'valor') {
      percEfetivo = totalOriginal > 0 ? Math.round((val / totalOriginal) * 10000) / 100 : 0;
      valorDireto = val;
    } else {
      percEfetivo = val;
    }

    // So o desconto ACIMA de 5% pede senha; o orcamento vive sempre no pix (cartao e decisao de
    // caixa — DA-168 revisado) e a margem do cartao ja e reposta no fechamento (DA-076/DA-274).
    // Compara o TOTAL que vai ser gravado com o piso de um 5% limpo (mesmo arredondamento), pra o
    // centavo de arredondamento em item barato nao estourar o teto falsamente (ver descontoAcimaDoLimite).
    const alvoTotal =
      valorDireto != null
        ? Math.round((totalOriginal - valorDireto) * 100) / 100
        : totalComDescontoPct(totalOriginal, percEfetivo);
    const acimaDoLimite = totalOriginal > 0 && alvoTotal < totalComDescontoPct(totalOriginal, 5) - 0.005;
    if (acimaDoLimite && !senhaGerencialUsada) {
      pedirSenhaGerencial(() => aplicarDescontoTotalInterno(percEfetivo, valorDireto));
      return;
    }
    // O campo Acrescimo NAO e' limpo: os dois convivem — o desconto sai do Bruto ja acrescido.
    aplicarDescontoTotalInterno(percEfetivo, valorDireto);
  }

  // Acrescimo mexeu na base: o desconto que ja estava no pedido e' reaplicado na MESMA porcentagem
  // sobre o Bruto novo (nunca some, nunca cresce alem do % ja autorizado). Em modo R$ o campo
  // passa a mostrar o %, porque o valor em reais deixou de ser o digitado.
  function reaplicarDescontoSobreBaseNova() {
    if (!(descontoTotalPerc > 0)) return;
    aplicarDescontoTotalInterno(descontoTotalPerc);
    if (descontoMode === 'valor') {
      setDescontoMode('perc');
      setDescontoInput(String(descontoTotalPerc));
    }
  }

  function aplicarDescontoTotalInterno(p: number, valorDireto: number | null = null) {
    clearOverride();
    setDescontoTotalPerc(Math.round(p * 100) / 100);
    setItens((prev) => {
      if (p === 0 && valorDireto == null) {
        return prev.map((item) => ({
          ...item,
          preco_unitario: item.preco_original,
          desconto_percentual: 0,
          desconto_valor: 0,
          subtotal: Math.round(item.quantidade * item.preco_original * 100) / 100,
        }));
      }

      const totalOrig = brutoLista(prev);
      if (totalOrig === 0) return prev;

      const targetRaw =
        valorDireto != null ? totalOrig - valorDireto : totalOrig * (1 - p / 100);

      // 3 faixas de arredondamento PRA CIMA (regra unica em @fiodecobre/shared).
      // Override de total exato (valorDireto) respeita os centavos digitados.
      const targetRounded =
        valorDireto != null
          ? Math.round(targetRaw * 100) / 100
          : arredondarTotalDesconto(targetRaw);

      const novosItens = prev.map((item) => {
        const peso = brutoLinha(item) / totalOrig;
        const subtotalItem = Math.round(targetRounded * peso * 100) / 100;
        const novoPreco =
          item.quantidade > 0 ? Math.round((subtotalItem / item.quantidade) * 100) / 100 : 0;
        // Piso ZERO: com o Bruto clampado, um desconto sobre o valor acrescido pode parar ACIMA
        // da tabela (5% de 500 = 475, tabela 463,10) — sem o piso a linha gravaria desconto
        // NEGATIVO. Espelha o `Math.max(0, ...)` que o caixa ja faz no finalizar.
        const descValor = Math.max(0, Math.round((item.preco_original - novoPreco) * 100) / 100);
        const descPerc =
          item.preco_original > 0
            ? Math.max(0, Math.round(((item.preco_original - novoPreco) / item.preco_original) * 10000) / 100)
            : 0;
        return {
          ...item,
          preco_unitario: novoPreco,
          desconto_percentual: descPerc,
          desconto_valor: descValor,
          subtotal: Math.round(item.quantidade * novoPreco * 100) / 100,
        };
      });

      const totalRecalc = novosItens.reduce((acc, it) => acc + it.subtotal, 0);
      const diff = Math.round((targetRounded - totalRecalc) * 100) / 100;
      if (diff !== 0 && novosItens.length > 0) {
        const last = novosItens.length - 1;
        const alvo = novosItens[last]!;
        const novoSub = Math.round((alvo.subtotal + diff) * 100) / 100;
        const novoPreco =
          alvo.quantidade > 0 ? Math.round((novoSub / alvo.quantidade) * 100) / 100 : 0;
        novosItens[last] = {
          ...alvo,
          subtotal: novoSub,
          preco_unitario: novoPreco,
          desconto_valor: Math.round((alvo.preco_original - novoPreco) * 100) / 100,
          desconto_percentual:
            alvo.preco_original > 0
              ? Math.round(((alvo.preco_original - novoPreco) / alvo.preco_original) * 10000) / 100
              : 0,
        };
      }

      return novosItens;
    });
  }

  function handleTotalClick() {
    if (edicaoBloqueadaRef.current) return; // travado (finalizado / consignacao / outro usuario editando)
    if (itens.length === 0) return;
    // Abre direto pra todos. A validacao da regra (acrescimo livre + desconto
    // ate 5% pra vendedora; admin/gerente sem limite) acontece na confirmacao.
    setEditandoTotal(true);
    setTotalOverrideInput('');
    setTimeout(() => totalInputRef.current?.focus(), 50);
  }

  function confirmarTotalOverride() {
    if (edicaoBloqueadaRef.current) { setEditandoTotal(false); return; } // DA-408
    const val = parseFloat(String(totalOverrideInput).replace(',', '.')) || 0;
    setEditandoTotal(false);
    if (val <= 0) return;
    setDescontoInput('');
    setAcrescimoInput('');

    // Caminho 1: acrescimo (Liquido digitado > Bruto). Sem trava pra ninguem
    // — vendedora ja pode acrescimo livre pelo campo Acrescimo do rodape.
    if (val > totalOriginal + 0.005) {
      // Medido contra a TABELA, que e' a base do rateio (aplicarAcrescimoValor) — contra o Bruto
      // atual, um pedido que ja tinha acrescimo fecharia abaixo do valor digitado.
      const acrescimoTotal = +(val - brutoTabela(itens)).toFixed(2);
      aplicarAcrescimoValor(acrescimoTotal, false);
      return;
    }

    // Caminho 2: desconto (Liquido digitado <= Bruto). Vendedora ate 5%;
    // acima exige senha. Admin/gerente sempre passa.
    const descValor = +(totalOriginal - val).toFixed(2);
    const percEfetivo = totalOriginal > 0 ? Math.round((descValor / totalOriginal) * 10000) / 100 : 0;
    // So desconto ACIMA de 5% pede senha (orcamento vive no pix; cartao reposto no fechamento — DA-168 revisado).
    // Compara o total digitado com o piso de um 5% limpo (mesmo arredondamento) — nao a % crua, que
    // em item barato estoura o teto por causa do centavo de arredondamento (ver descontoAcimaDoLimite).
    // Admin/gerente NAO desvia daqui: quem libera e' o `pedirSenhaGerencial`, e e' ele que CARIMBA o
    // orcamento. Ate 2026-07-31 o admin era atalhado antes da porta e o carimbo nunca era gravado —
    // o desconto entrava e o "Enviar ao Caixa" (que so le o carimbo) barrava o proprio autor. DA-405.
    // Condicao IDENTICA a do campo Desconto (handleAplicarDesconto): so `usada`, NUNCA `persistida`.
    // Aplicar desconto novo exige autorizacao NOVA — o gerente autorizou 6% naquele pedido, nao os
    // 30% que alguem digitar depois. `persistida` so serve pra DEIXAR SAIR o que ja foi autorizado.
    const acimaDoLimite = totalOriginal > 0 && val < totalComDescontoPct(totalOriginal, 5) - 0.005;
    if (acimaDoLimite && !senhaGerencialUsada) {
      pedirSenhaGerencial(() => aplicarDescontoTotalInterno(percEfetivo, descValor));
      return;
    }
    aplicarDescontoTotalInterno(percEfetivo, descValor);
  }

  // Override de base manual (Ctrl+Shift+X) — fixa o valor da venda a mao (uso raro).
  // Ratea V entre os itens pela proporcao atual e zera o desconto: cada item passa a
  // valer sua fatia de V (preco_original = preco_unitario), entao Bruto = Liquido = V,
  // sem desconto fantasma. A comissao deriva sozinha de V (DA-169 tira o cartao). O
  // valor congela o preco-vivo no backend e a finalizacao respeita (nao re-infla).
  function aplicarOverrideBase(v: number) {
    if (v <= 0 || itens.length === 0) return;
    const baseAtual = itens.reduce((acc, it) => acc + it.preco_original * it.quantidade, 0);
    if (baseAtual <= 0) return;
    const alvo = Math.round(v * 100) / 100;
    setOverrideAuto(Math.round(baseAtual * 100) / 100);
    setOverrideBase(alvo);
    setDescontoTotalPerc(0);
    setDescontoInput('');
    setAcrescimoInput('');
    setItens((prev) => {
      const base = prev.reduce((acc, it) => acc + it.preco_original * it.quantidade, 0);
      if (base <= 0) return prev;
      const novos = prev.map((item) => {
        const peso = (item.preco_original * item.quantidade) / base;
        const sub = Math.round(alvo * peso * 100) / 100;
        const unit = item.quantidade > 0 ? Math.round((sub / item.quantidade) * 10000) / 10000 : 0;
        return {
          ...item,
          preco_original: unit,
          // O override ja fixava a base a mao; agora ele tambem CARIMBA a linha. Antes dependia
          // so do `override_base` do orcamento — que o `clearOverride` derruba no desconto
          // seguinte, deixando o reajuste livre pra desfazer o que o gerente travou.
          base_manual: true,
          preco_unitario: unit,
          desconto_percentual: 0,
          desconto_valor: 0,
          subtotal: Math.round(item.quantidade * unit * 100) / 100,
        };
      });
      // Correcao de centavo no ultimo item — fecha exato em V.
      const soma = novos.reduce((acc, it) => acc + it.subtotal, 0);
      const diff = Math.round((alvo - soma) * 100) / 100;
      if (diff !== 0 && novos.length > 0) {
        const last = novos.length - 1;
        const alvoItem = novos[last]!;
        const novoSub = Math.round((alvoItem.subtotal + diff) * 100) / 100;
        const novoUnit = alvoItem.quantidade > 0 ? Math.round((novoSub / alvoItem.quantidade) * 10000) / 10000 : 0;
        novos[last] = { ...alvoItem, subtotal: novoSub, preco_original: novoUnit, preco_unitario: novoUnit };
      }
      return novos;
    });
    toast.sucesso(`Base da venda fixada em R$ ${fmt(alvo)}`);
  }

  // Qualquer edicao normal de preco/desconto/quantidade/itens derruba o override:
  // a base manual deixou de valer e nao pode persistir desatualizada.
  function clearOverride() {
    if (overrideBase != null) {
      setOverrideBase(null);
      setOverrideAuto(null);
    }
  }

  // Preco de TABELA da linha — a base de todo acrescimo. Produto sem preco de cartao no cadastro
  // nao tem tabela pra onde voltar, e ai a base e' o preco que a linha ja tem.
  const precoTabelaOuAtual = (it: { preco_tabela: number; preco_original: number }) =>
    Number(it.preco_tabela) > 0 ? Number(it.preco_tabela) : it.preco_original;
  const brutoTabela = (lista: ItemOrc[]) =>
    lista.reduce((acc, it) => acc + Math.round(precoTabelaOuAtual(it) * it.quantidade * 100), 0) / 100;

  // Ratea um acrescimo R$ entre os itens, proporcional a (preco de TABELA × qtd), com correcao de
  // centavo no ultimo item pra fechar `bruto de tabela + acrescimoTotal` exato.
  // ⚠️ A base e' a TABELA, nunca o `preco_original` atual: ele ja carrega o acrescimo anterior, e
  // rodar de novo (o campo segue preenchido depois do Enter e reaplica no desfoque; ou 10% trocado
  // por 5%) somava acrescimo sobre acrescimo. Sobre a tabela, o mesmo valor da sempre o mesmo preco.
  // `manterDesconto`: pelo campo Acrescimo o desconto do pedido e' reaplicado por cima; pelo Liquido
  // digitado (valor final exato) nao, porque ali o numero digitado ja e' o total.
  function aplicarAcrescimoValor(acrescimoTotal: number, manterDesconto = true) {
    if (edicaoBloqueadaRef.current) return; // DA-408 — porta de tras: chamada tambem pelo Liquido
    if (acrescimoTotal <= 0) return;
    clearOverride();
    if (!manterDesconto) {
      setDescontoTotalPerc(0);
      setDescontoInput('');
    }
    setItens((prev) => {
      const totalBase = brutoTabela(prev);
      if (totalBase === 0) return prev;
      const novosItens = prev.map((item) => {
        const base = precoTabelaOuAtual(item);
        const peso = Math.round(base * item.quantidade * 100) / 100 / totalBase;
        const acrescimoItem = (acrescimoTotal * peso) / item.quantidade;
        const novoPreco = Math.round((base + acrescimoItem) * 100) / 100;
        return {
          ...item,
          // O acrescimo SOBE a base, igual a edicao manual do preco: senao o Bruto ficaria na
          // tabela velha e o rodape exibiria Liquido maior que Bruto com Desconto R$ 0,00.
          preco_original: novoPreco,
          base_manual: true,
          preco_unitario: novoPreco,
          desconto_percentual: 0,
          desconto_valor: 0,
          subtotal: Math.round(item.quantidade * novoPreco * 100) / 100,
        };
      });
      // Correcao de centavo no ultimo item — fecha exato Bruto+acrescimo
      const alvo = +(totalBase + acrescimoTotal).toFixed(2);
      const totalRecalc = novosItens.reduce((acc, it) => acc + it.subtotal, 0);
      const diff = Math.round((alvo - totalRecalc) * 100) / 100;
      if (diff !== 0 && novosItens.length > 0) {
        const last = novosItens.length - 1;
        const item = novosItens[last]!;
        const novoSub = Math.round((item.subtotal + diff) * 100) / 100;
        const novoPreco = item.quantidade > 0 ? Math.round((novoSub / item.quantidade) * 100) / 100 : 0;
        // A base acompanha o centavo tambem — se so o preco_unitario levasse o residuo, a linha
        // nasceria com um desconto (ou acrescimo) de 1 centavo que ninguem pediu.
        novosItens[last] = { ...item, subtotal: novoSub, preco_original: novoPreco, preco_unitario: novoPreco };
      }
      return novosItens;
    });
    if (manterDesconto) reaplicarDescontoSobreBaseNova();
  }

  // Tirar o acrescimo: toda linha volta ao preco de TABELA (o mesmo gesto de digitar 0 no Preco
  // Unit., aplicado ao pedido inteiro). O desconto do pedido fica, reaplicado sobre a tabela.
  function removerAcrescimo() {
    if (edicaoBloqueadaRef.current) return; // DA-408
    acrescimoApagadoRef.current = false;
    setAcrescimoInput('');
    if (itens.length === 0) return;
    clearOverride();
    setItens((prev) =>
      prev.map((it) => {
        const tabela = precoTabelaOuAtual(it);
        return {
          ...it,
          preco_original: tabela,
          base_manual: Number(it.preco_tabela) > 0 ? false : it.base_manual,
          preco_unitario: tabela,
          desconto_percentual: 0,
          desconto_valor: 0,
          subtotal: Math.round(it.quantidade * tabela * 100) / 100,
        };
      }),
    );
    reaplicarDescontoSobreBaseNova();
    toast.sucesso('Acrescimo retirado — os itens voltaram ao preco de tabela.');
  }

  function aplicarAcrescimo() {
    if (edicaoBloqueadaRef.current) return; // DA-408 — ver handleAplicarDesconto
    const texto = acrescimoInput.trim();
    if (texto === '') {
      if (acrescimoApagadoRef.current) removerAcrescimo();
      return;
    }
    const val = parseFloat(texto) || 0;
    if (val <= 0) {
      removerAcrescimo();
      return;
    }
    const totalBase = brutoTabela(itens);
    const acrescimoTotal = acrescimoMode === 'valor' ? val : totalBase * (val / 100);
    aplicarAcrescimoValor(acrescimoTotal);
  }

  async function selecionarTipoNF(tipo: 'nfce' | 'nfe', cliId?: number) {
    const id = cliId !== undefined ? cliId : clienteId;
    if (tipo === 'nfe') {
      if (id === 1) return;
      setNfeValidando(true);
      try {
        const res = await api.get<{ valido: boolean; faltam: string[] }>(`/api/nfe/validar-cliente/${id}`);
        setNfeValidacao(res);
        setTipoNF(res.valido ? 'nfe' : 'nfce');
      } catch {
        setNfeValidacao({ valido: false, faltam: ['Erro ao validar cadastro do cliente.'] });
        setTipoNF('nfce');
      } finally {
        setNfeValidando(false);
      }
    } else {
      setTipoNF('nfce');
      setNfeValidacao({ valido: true, faltam: [] });
    }
  }

  function handleEnviarCaixa() {
    if (itens.length === 0) {
      alert('Adicione pelo menos um item');
      return;
    }
    // Consignacao: "Enviar ao Caixa" AFIRMA que e' venda (o backend recusa remessa no caixa).
    // Flipa o tipo -> o autosave (deps ja incluem tipoDoc) grava 'venda' antes do envio.
    if (!ehAcerto && tipoDoc !== 'venda') setTipoDoc('venda');
    // Trava: orcamento com desconto acima de 5% so vai ao caixa com autorizacao gerencial.
    // `isAdmin` e' a rede de seguranca pra pedido ANTIGO que ficou sem carimbo (ver DA-405): admin/gerente
    // ja podem dar o desconto por todas as portas, e o backend (caixa.routes) tambem os libera — sem isto
    // a trava de tela ficava MAIS rigida que o sistema e prendia o admin do lado de fora do proprio desconto.
    if (descontoAcimaDoLimite() && !isAdmin && !senhaGerencialUsada && !senhaGerencialPersistida) {
      alerta(
        'Este orcamento esta com desconto acima de 5%. Para enviar ao caixa e preciso autorizacao de um gerente ou administrador.',
        { titulo: 'Desconto acima do permitido', tipo: 'erro' },
      );
      return;
    }
    if (total < 1) {
      setShowModalVendaMinima(true);
      return;
    }
    setShowModalEnviar(true);
  }

  // `handleVendaCm`/`confirmarVendaCm` sairam daqui na DA-415 — a liberacao passou pro modal
  // "Finalizar venda" do caixa (ModalFinalizarVenda.tsx). O que SOBRA nesta tela e a vida do
  // venda_cm DEPOIS de liberado: o banner laranja e o "Registrar devolucao".

  async function confirmarEnviarCaixa(tipoDocumento: string, pagamentos: PagamentoEnvio[], cpfNota: string | null) {
    if (enviandoRef.current) return; // ja tem um envio em curso — ignora o clique repetido
    enviandoRef.current = true;
    setEnviando(true);
    try {
      // O que o autosave ainda deve tem que estar no banco ANTES do envio: o enviar-caixa le o
      // pedido gravado, nao o que esta na tela ([DA-697]).
      await gravarPendencias();
      let orcId = savedId;
      if (!orcId) {
        const res = await api.post<{ id: number }>('/api/orcamentos', {
          cliente_id: clienteId,
          // o caixa e' sempre venda ou acerto — NUNCA remessa (a remessa nao passa por aqui)
          tipo: ehAcerto ? 'acerto' : 'venda',
          desconto_total_percentual: 0,
          senha_gerencial_usada: senhaGerencialUsada,
          observacoes,
          comprador: comprador || null,
          prestador_id: prestadorId,
          status: 'salvo',
          override_base: overrideBase,
          override_base_auto: overrideAuto,
          itens: itens.map((item) => ({
            produto_id: item.produto_id,
            quantidade: item.quantidade,
            preco_original: item.preco_original,
            base_manual: item.base_manual,
            preco_unitario: item.preco_unitario,
            desconto_percentual: item.desconto_percentual,
            desconto_valor: item.desconto_valor,
            subtotal: item.subtotal,
          })),
        });
        orcId = res.id;
      }
      // `confirmar_estoque` so vai TRUE na 2a tentativa, depois do operador ver a lista de faltas
      // e decidir seguir. A 1a chamada sempre pergunta (DA-329) — e o backend desfez o envio,
      // entao o pedido nao ficou preso no caixa enquanto ele pensa.
      const enviar = (confirmarEstoque: boolean) =>
        api.post(`/api/orcamentos/${orcId}/enviar-caixa`, {
          tipo_documento: tipoDocumento,
          pagamentos,
          cpf_nota: cpfNota || null,
          ...(confirmarEstoque ? { confirmar_estoque: true } : {}),
        });

      try {
        await enviar(false);
      } catch (e: any) {
        const faltas: string[] | undefined = e?.corpo?.faltas;
        if (e?.status !== 409 || !faltas?.length) throw e;
        const ok = await confirmar(
          `Estoque nao cobre estes itens:\n\n- ${faltas.join('\n- ')}\n\n` +
            'Enviar assim mesmo? O estoque destes produtos vai ficar negativo — o que costuma ' +
            'significar entrada nao lancada. Se a quantidade estiver errada, cancele e corrija o pedido.',
          { titulo: 'Estoque insuficiente', textoConfirmar: 'Enviar assim mesmo', tipo: 'aviso' },
        );
        if (!ok) return;
        await enviar(true);
      }
      setShowModalEnviar(false);
      toast.sucesso('Orcamento enviado ao caixa');
      navigate('/orcamentos');
    } catch (err: any) {
      const msg = err?.message || 'Erro ao enviar ao caixa';
      // Erros do enviar-caixa (estoque insuficiente, dados de cliente faltando)
      // sao bloqueantes e multi-linha — modal central com OK e mais legivel.
      setShowModalEnviar(false);
      alerta(msg, { titulo: 'Nao foi possivel enviar ao caixa', tipo: 'erro' });
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
    }
  }

  function handleBuscaKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!showSugestoes || produtosSugestao.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSugestaoIdx((prev) => {
        const next = Math.min(prev + 1, produtosSugestao.length - 1);
        scrollToItem(next);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSugestaoIdx((prev) => {
        const next = Math.max(prev - 1, 0);
        scrollToItem(next);
        return next;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const idx = sugestaoIdx >= 0 ? sugestaoIdx : 0;
      const p = produtosSugestao[idx];
      if (p) adicionarProduto(p);
    } else if (e.key === 'Escape') {
      setShowSugestoes(false);
      setSugestaoIdx(-1);
    }
  }

  const tabelaRef = useRef<HTMLDivElement>(null);
  function handleEnterComoTab(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter' && e.key !== 'Tab') return;
    if (e.key === 'Tab' && e.shiftKey) return;
    e.preventDefault();
    const inputs = tabelaRef.current?.querySelectorAll<HTMLInputElement>('input:not([disabled])');
    if (!inputs) return;
    const arr = Array.from(inputs);
    const idx = arr.indexOf(e.target as HTMLInputElement);
    if (idx >= 0 && idx < arr.length - 1) {
      arr[idx + 1]!.focus();
      arr[idx + 1]!.select();
    } else {
      if (ultimaBuscaRef.current) setBuscaProduto(ultimaBuscaRef.current);
      setTimeout(() => {
        buscaProdutoRef.current?.focus();
        buscaProdutoRef.current?.select();
      }, 0);
    }
  }

  function scrollToItem(idx: number) {
    if (!sugestoesRef.current) return;
    const items = sugestoesRef.current.children;
    items[idx]?.scrollIntoView({ block: 'nearest' });
  }

  function handleSenhaAprovada() {
    setSenhaGerencialUsada(true);
    setShowModalSenha(false);
    if (senhaCallback) senhaCallback();
  }

  // Fechar menu ao clicar fora
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Setas globais navegam grid de itens
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (e.defaultPrevented) return;
      if (showModalSenha || showModalEnviar || showModalWhatsApp || showModalListaClientes || showModalOverride) return;
      if (itens.length === 0) return;
      e.preventDefault();
      if (e.key === 'ArrowDown') {
        setHoverIdx((idx) => (idx == null || idx < 0 ? 0 : Math.min(idx + 1, itens.length - 1)));
      } else {
        setHoverIdx((idx) => (idx == null || idx < 0 ? 0 : Math.max(idx - 1, 0)));
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [itens.length, showModalSenha, showModalEnviar, showModalWhatsApp, showModalListaClientes, showModalOverride]);

  // Atalho OCULTO Ctrl+Shift+X — override de base manual (uso raro, so admin/gerente).
  // Pede senha gerencial pra abrir (admin/gerente passam direto). Ver project_override_base_parcelamento.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.ctrlKey && e.shiftKey && (e.key === 'X' || e.key === 'x'))) return;
      if (edicaoBloqueadaRef.current) return; // DA-408: nem o atalho oculto fura documento travado
      if (showModalSenha || showModalEnviar || showModalWhatsApp || showModalListaClientes || showModalOverride) return;
      if (itens.length === 0) return;
      e.preventDefault();
      pedirSenhaGerencial(() => setShowModalOverride(true));
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itens.length, showModalSenha, showModalEnviar, showModalWhatsApp, showModalListaClientes, showModalOverride]);

  useEffect(() => {
    if (hoverIdx == null || hoverIdx < 0) return;
    itemRefs.current[hoverIdx]?.scrollIntoView({ block: 'nearest' });
  }, [hoverIdx]);

  const clienteNomeEfetivo =
    clienteId !== 1 ? clienteNome : buscaCliente.trim().toUpperCase() || clienteNome;

  function montarDadosPdf() {
    return {
      numero: orcamentoNumero,
      // Emissao e vendedor vem do ORCAMENTO, nao de quem esta imprimindo (DA-397).
      created_at: orcCriadoEm || new Date().toISOString(),
      vendedor_nome: orcVendedorNome || usuario?.login || usuario?.nome || '',
      cliente_nome: clienteNomeEfetivo,
      forma_pagamento_nome: formasPagamento.find((f) => f.id === formaPagamentoId)?.nome || '',
      subtotal,
      desconto_total_percentual: descontoTotalPerc,
      desconto_total_valor: subtotal * (descontoTotalPerc / 100),
      total,
      observacoes,
      // subtotal EXIBIDO (calibrado quando Pix): os itens somam EXATAMENTE o total quebrado.
      itens: itens.map((it, idx) => ({
        codigo_interno: it.codigo_interno,
        produto_descricao: it.descricao,
        marca_nome: it.marca_nome,
        unidade_sigla: it.unidade_sigla,
        quantidade: it.quantidade,
        preco_original: it.preco_original,
        preco_unitario: it.preco_unitario,
        desconto_percentual: it.desconto_percentual,
        subtotal: subtotaisExibidos[idx] ?? it.subtotal,
        foto_url: it.foto_url,
      })),
    };
  }

  async function handleSalvarPdf() {
    setShowMenu(false);
    if (!orcamentoNumero) {
      alert('Salve o orcamento antes de gerar o PDF.');
      return;
    }
    try {
      const doc = await gerarPdfOrcamento(montarDadosPdf());
      doc.save(`${orcamentoNumero}.pdf`);
    } catch (err: any) {
      alert('Erro ao gerar PDF: ' + (err?.message || err));
    }
  }

  async function handleVisualizarPdf() {
    setShowMenu(false);
    if (!orcamentoNumero) {
      alert('Salve o orcamento antes de visualizar.');
      return;
    }
    try {
      const doc = await gerarPdfOrcamento(montarDadosPdf());
      const url = doc.output('bloburl');
      window.open(String(url), '_blank');
    } catch (err: any) {
      alert('Erro ao gerar PDF: ' + (err?.message || err));
    }
  }

  // `forcarPdf`: o item "Enviar PDF" do menu de tres pontos manda o PDF mesmo com a caixa Fly
  // marcada — o rotulo dele promete o PDF.
  async function handleEnviarWhatsApp(forcarPdf = false) {
    setShowMenu(false);
    if (!comFly || forcarPdf) {
      setFlyPronto(null);
      setShowModalWhatsApp(true);
      return;
    }
    // Fly com o preco DO ORCAMENTO (pedido dele, 07/10/2026): o do chat anunciava o preco Pix do
    // cadastro e o orcamento cobrava outro. A arte sai do servidor (mesma peca do chat) lendo as
    // linhas GRAVADAS — por isso grava o que o autosave ainda deve ANTES de pedir a arte, senao o
    // fly sairia com o preco de antes da ultima edicao.
    if (!savedId) {
      alerta('Salve o orcamento antes de enviar o fly.', { titulo: 'Orcamento nao salvo', tipo: 'aviso' });
      return;
    }
    if (itens.length === 0 || itens.length > 3) {
      alerta(
        itens.length === 0
          ? 'O orcamento nao tem produtos.'
          : `O fly vai com no maximo 3 produtos — este orcamento tem ${itens.length}. Desmarque "Fly" pra enviar o orcamento.`,
        { titulo: 'Fly', tipo: 'aviso' },
      );
      return;
    }
    setGerandoFly(true);
    try {
      await gravarPendencias();
      const r = await api.post<{ imagem_base64: string; legenda: string; filename: string }>(
        `/api/orcamentos/${savedId}/fly-previa`, {},
      );
      setFlyPronto(r);
      setShowModalWhatsApp(true);
    } catch (e: any) {
      alerta(e?.corpo?.erro ?? e?.message ?? 'Nao consegui gerar o fly.', { titulo: 'Fly', tipo: 'erro' });
    } finally {
      setGerandoFly(false);
    }
  }

  async function handleFazerCopia() {
    setShowMenu(false);
    if (!savedId) {
      alert('Salve o orcamento antes de copiar.');
      return;
    }
    try {
      const data = await api.post<{ id: number }>('/api/orcamentos', {
        cliente_id: clienteId,
        tipo: ehAcerto ? 'acerto' : tipoDoc,
        forma_pagamento_id: formaPagamentoId,
        observacoes,
        comprador: comprador || null,
        prestador_id: prestadorId,
        senha_gerencial_usada: senhaGerencialUsada,
        tipo_nf: tipoNF,
        status: 'salvo',
        itens: itens.map((it) => ({
          produto_id: it.produto_id,
          quantidade: it.quantidade,
          preco_original: it.preco_original,
          base_manual: it.base_manual,
          preco_unitario: it.preco_unitario,
          desconto_percentual: it.desconto_percentual || 0,
          desconto_valor: it.desconto_valor || 0,
          subtotal: it.subtotal,
        })),
      });
      navigate(`/orcamentos/${data.id}`);
    } catch (err: any) {
      alert('Erro ao copiar: ' + (err?.message || err));
    }
  }

  // Estilos do tema Orcamento (CSS vars)
  const orcStyle = {
    bg: 'var(--orc-bg)',
    fieldBg: 'var(--orc-field-bg)',
    fieldFg: 'var(--orc-field-fg)',
    fieldSize: 'var(--orc-field-size)',
    fieldWeight: 'var(--orc-field-weight)',
    headerBg: 'var(--orc-header-bg)',
    headerFg: 'var(--orc-header-fg)',
    headerSize: 'var(--orc-header-size)',
    headerWeight: 'var(--orc-header-weight)',
    rowBg: 'var(--orc-row-bg)',
    rowAltBg: 'var(--orc-row-alt-bg)',
    rowFg: 'var(--orc-row-fg)',
    rowSize: 'var(--orc-row-size)',
    rowWeight: 'var(--orc-row-weight)',
    selectBg: 'var(--orc-select-bg)',
    selectFg: 'var(--orc-select-fg)',
    imgSize: 'var(--orc-img-size)',
    totalBg: 'var(--orc-total-bg)',
    totalFg: 'var(--orc-total-fg)',
    totalSize: 'var(--orc-total-size)',
    totalWeight: 'var(--orc-total-weight)',
  };

  if (loadingEdit) return <div className="text-center py-8 text-gray-400">Carregando...</div>;

  return (
    // Margem de cima/baixo apertada SO nesta tela (pedido dele, 02/10/2026, com print da tela
    // 1366x768 da Claudia): o .app-content da 20px nas duas pontas e cada pixel aqui e' altura da
    // lista de produtos. Os -14px deixam 6px; a altura soma o que a margem negativa tirou.
    <div className="flex flex-col overflow-hidden" style={{ backgroundColor: orcStyle.bg, margin: '-14px 0', height: 'calc(100% + 28px)' }}>
      {/* Header */}
      <div className="pagina-header flex-shrink-0" style={{ marginBottom: 6, flexWrap: 'wrap', rowGap: 6 }}>
        {/* 14px negrito e sem o "N°" — pedido dele, 08/10/2026, com print. Excecao consciente ao
            padrao de titulo de 20px: nesta tela o titulo divide a faixa com as buscas. */}
        <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, order: 0, flexShrink: 0, fontSize: 14, fontWeight: 700 }}>
          <button
            type="button"
            onClick={() => flushAndNavigate(voltarPara, voltarState)}
            className="btn-icon"
            title={voltarPara === '/orcamentos' ? 'Voltar para lista de orcamentos' : 'Voltar'}
            aria-label="Voltar"
            style={{ marginRight: 4 }}
          >
            <ArrowLeft size={20} />
          </button>
          {orcamentoNumero ? orcamentoNumero.replace('ORC-', '') : 'Novo Orcamento'}
          {/* Os selos "Salvando..." / "Salvo" SAIRAM (pedido dele, 08/10/2026, com print): o espaco
              foi pro campo Produto. ⚠️ O "Salvo" era tambem o unico sinal de autosave FALHANDO (a
              falha so' o apagava) — por isso a falha ganhou selo proprio, que so' aparece nela. */}
          {saveStatus === 'error' && (
            <span
              className="flex items-center gap-1 text-xs"
              style={{ backgroundColor: '#f97316', color: '#000', fontWeight: 600, padding: '2px 8px', borderRadius: 6 }}
              title="A ultima alteracao nao foi gravada. Ela sera tentada de novo na proxima mudanca."
            >
              Nao salvo
            </span>
          )}
          {savedId && (
            <div ref={menuRef} className="relative">
              <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 hover:bg-gray-200 rounded-lg transition text-gray-500">
                <MoreVertical size={18} />
              </button>
              {showMenu && (
                <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[220px] z-50">
                  {/* MODO EMERGENCIA: o menu inteiro precisa do servidor — o PDF sairia com o numero
                      provisorio EMG-..., que nao e' o ORC que o pedido ganha ao subir (decisao do Ruan). */}
                  <button onClick={handleVisualizarPdf} disabled={emergencia} title={emergencia ? MSG_EMERGENCIA : undefined} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
                    <Eye size={16} className="text-purple-600" />
                    Visualizar Impr Orcamento
                  </button>
                  <button onClick={handleSalvarPdf} disabled={emergencia} title={emergencia ? MSG_EMERGENCIA : undefined} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
                    <FileDown size={16} className="text-blue-600" />
                    Salvar em PDF
                  </button>
                  <button onClick={() => void handleEnviarWhatsApp(true)} disabled={emergencia} title={emergencia ? MSG_EMERGENCIA : undefined} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
                    <MessageCircle size={16} className="text-green-600" />
                    Enviar PDF
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button onClick={handleFazerCopia} disabled={emergencia} title={emergencia ? MSG_EMERGENCIA : undefined} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed">
                    <Copy size={16} className="text-orange-500" />
                    Fazer Copia
                  </button>
                  {/* A Venda CM SAIU daqui (DA-415, cartao RDS 12.4). Ela virou o terceiro botao do
                      modal "Finalizar venda", no caixa, so pra admin. Nao foi arrumacao de tela:
                      liberar por aqui obrigava o admin a ABRIR o pedido de outra pessoa, e o
                      autosave regrava `vendedor_id = quem abriu` (DA-397/DA-406) — o que desviava
                      a comissao daquele pedido, calado, ate o fechamento do mes. Alem disso o selo
                      HABILITA e nao determina: o mesmo cliente de obra as vezes paga a vista, e
                      quem descobre isso e quem esta no caixa, nao quem monta o orcamento. */}
                </div>
              )}
            </div>
          )}
        </h2>
        <div className="pagina-acoes" style={{ order: 3, marginLeft: 'auto' }}>
          {/* A caixa "Nao quis informar o Telefone" que morava aqui SAIU (pedido dele, 08/10/2026,
              com print): o espaco dela foi pro campo Produto. A recusa continua tendo porta — o
              caminho 3 do modal "Enviar ao Caixa" grava a marca junto com nome e etiqueta
              ([DA-780]), e desde entao a caixa daqui so' pre-marcava esse caminho. */}
          <button
            onClick={() => flushAndNavigate('/orcamentos')}
            className="flex items-center gap-1.5 text-sm font-bold text-yellow-300 bg-blue-700 hover:bg-blue-800 rounded-lg px-3 py-1.5 transition"
            title="Pesquisar orcamentos"
          >
            <Search size={14} />
            {/* Rotulo curto (pedido dele, 08/10/2026): era "Pesquisar Orcamento"; a largura que
                sobrou foi pro campo Cliente. O que o botao faz segue no `title`. */}
            Orçamento
          </button>
          {/* "Fly" — pedido dele, 07/10/2026, no espaco entre "Pesquisar Orcamento" e "WhatsApp".
              Marcada, o WhatsApp manda o fly do produto com o preco DESTE orcamento (ate 3
              produtos), no lugar do PDF. Fecha a divergencia fly x orcamento pela raiz: o
              numero que o cliente recebe e' o que o caixa cobra. */}
          <label
            className="flex items-center gap-1.5 text-sm font-semibold cursor-pointer select-none"
            title="Marcado: o WhatsApp envia o fly do produto com o preco deste orcamento (ate 3 produtos), em vez do PDF."
          >
            <input
              type="checkbox"
              checked={comFly}
              onChange={(e) => setComFly(e.target.checked)}
              disabled={emergencia}
              className="w-4 h-4 cursor-pointer"
            />
            Fly
          </label>
          <button
            onClick={() => void handleEnviarWhatsApp()}
            disabled={gerandoFly || emergencia}
            title={emergencia ? MSG_EMERGENCIA : undefined}
            className="flex items-center gap-2 bg-green-500 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-green-600 transition disabled:opacity-60"
          >
            {gerandoFly ? <Loader2 size={16} className="animate-spin" /> : <MessageCircle size={16} />}
            {gerandoFly ? 'Montando o fly...' : 'WhatsApp'}
          </button>
          {/* Venda / acerto -> caixa (verde). */}
          <button
            onClick={handleEnviarCaixa}
            disabled={itens.length === 0 || (bloqueadoEdicao && !ehAcerto) || emergencia}
            title={
              emergencia
                ? 'Indisponivel no modo emergencia — o orcamento vai ao caixa depois que o sistema voltar'
                : bloqueadoEdicao && !ehAcerto
                ? edicaoDeOutro
                  ? `${edicaoDeOutro} esta editando este orcamento — somente leitura`
                  : 'Orcamento ja finalizado — travado'
                : 'Enviar ao caixa'
            }
            className="flex items-center gap-2 bg-green-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={16} />
            {/* Era "Enviar ao Caixa" — rotulo curto pedido dele em 08/10/2026 (mesma leva do "Orçamento"). */}
            Caixa
          </button>
        </div>
        {/* Produto + Cliente na MESMA faixa do titulo (pedido dele, 02/10/2026): eram duas faixas
            empilhadas, e cada uma e' um produto a menos na lista. A ordem na tela vem do `order`
            (titulo, produto, cliente, botoes); em janela estreita os botoes descem pra uma segunda
            linha da mesma faixa (flex-wrap) em vez de espremer as buscas. */}
            {/* Largura Produto x Cliente (pedidos dele, 08/10/2026): o Produto ganhou 262px de base
                (a caixa "Nao quis informar o Telefone" 187+8 e o selo "Salvo" 59+8), o Cliente 119px
                (o que os botoes "Orçamento"/"Caixa" encolheram), e o resto se dividia 3:2. Depois ele
                mandou tirar 15% do Produto e dar ao Cliente — em QUALQUER largura de janela, por isso
                a regra vira os numeros abaixo e nao um ajuste fixo:
                Produto = 0,85 x (262 + 3k) = 222,7 + 2,55k   |   Cliente = (119 + 2k) + 0,15 x (262 + 3k) = 158,3 + 2,45k
                (k = sobra da faixa / 5). Mexer num lado exige refazer a conta dos dois. */}
            <div className="flex items-center gap-2" style={{ order: 1, minWidth: 260, flex: '2.55 1 222.7px' }}>
              <label className="text-sm font-medium text-gray-600 flex-shrink-0">Produto</label>
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  ref={buscaProdutoRef}
                  type="text"
                  value={buscaProduto}
                  onChange={(e) => setBuscaProduto(upperMantendoCursor(e))}
                  onFocus={(e) => e.target.select()}
                  onKeyDown={handleBuscaKeyDown}
                  disabled={bloqueadoEdicao}
                  placeholder={bloqueadoEdicao ? 'Documento travado' : 'Buscar por codigo ou descricao...'}
                  className="w-full pl-9 pr-11 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: orcStyle.fieldBg,
                    color: orcStyle.fieldFg,
                    fontSize: `calc(${orcStyle.fieldSize} * 1px)`,
                    fontWeight: orcStyle.fieldWeight as any,
                  }}
                  autoFocus
                />
                {/* A camera e uma PORTA ALTERNATIVA pro mesmo campo (DA-392), pra quem tem a
                    peca na mao e nao sabe como a loja a cadastrou. O que ela devolve entra
                    pelo MESMO `adicionarProduto` do autocomplete de texto — se o caminho
                    fosse outro, as duas portas divergiriam com o tempo. A peca some sozinha
                    em aparelho sem camera ou com o motor fora do ar. */}
                {!bloqueadoEdicao && (
                  <ReconhecerProdutoCamera
                    tela="orcamento"
                    somenteVendaveis
                    onEscolher={adicionarProduto}
                  />
                )}
                {showSugestoes && produtosSugestao.length > 0 && sugestoesRect && (
                  <div
                    ref={sugestoesRef}
                    className="fixed z-50 border border-gray-200 rounded-lg shadow-2xl overflow-y-auto"
                    style={{
                      top: sugestoesRect.top,
                      left: sugestoesRect.left,
                      width: sugestoesRect.width,
                      maxHeight: `calc(100vh - ${sugestoesRect.top + 16}px)`,
                      backgroundColor: orcStyle.rowBg,
                    }}
                  >
                    {produtosSugestao.map((p, idx) => {
                      const selected = idx === sugestaoIdx;
                      const tambemChamado = outraDescricaoQueCasou(buscaProduto, p.descricao, p.outras_descricoes);
                      return (
                        <button
                          key={p.id}
                          onClick={() => adicionarProduto(p)}
                          onMouseEnter={() => setSugestaoIdx(idx)}
                          className="w-full text-left px-4 py-3 text-base border-b border-gray-100 flex items-center gap-4"
                          style={{
                            backgroundColor: selected
                              ? orcStyle.selectBg
                              : idx % 2 === 0
                              ? orcStyle.rowBg
                              : orcStyle.rowAltBg,
                            color: selected ? orcStyle.selectFg : orcStyle.rowFg,
                          }}
                        >
                          {p.foto_url ? (
                            <FotoProduto url={p.foto_url} tam={56} style={{ flexShrink: 0 }} />
                          ) : (
                            <div className="w-14 h-14 bg-gray-100 rounded border border-gray-200 flex items-center justify-center flex-shrink-0">
                              <span className="text-gray-300 text-xs">Sem foto</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            {produtoInativo(p) && <SeloInativo />}
                            <span className="font-mono text-blue-600 mr-2">{p.codigo_interno}</span>
                            <span>{p.descricao}</span>
                            <span className="opacity-60 ml-2">({p.unidade_sigla})</span>
                            {p.marca_nome && (
                              <span className="ml-2">{p.marca_nome}</span>
                            )}
                            {/* Achado pelo nome que o cliente usa: diz qual, pra a vendedora
                                aprender o nome do cadastro (Outras Descricoes, 02/10/2026). */}
                            {tambemChamado && (
                              <div className="text-sm opacity-70">tambem chamado de {tambemChamado}</div>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-semibold">R$ {fmt4(p.preco_cartao)}</div>
                            <div className="text-sm opacity-60">Est: <EstoqueEfetivo
                              proprio={p.estoque_atual != null ? Number(p.estoque_atual) : null}
                              efetivo={p.estoque_efetivo != null ? Number(p.estoque_efetivo) : null}
                              compacto
                            /></div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Conta da largura no comentario do campo Produto, acima. */}
            <div className="flex items-center gap-2" style={{ order: 2, minWidth: 220, flex: '2.45 1 158.3px' }}>
              <label className="text-sm font-medium text-gray-600 flex-shrink-0">Cliente</label>
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                {clienteId !== 1 && buscaCliente.trim() === (clienteNome || '').trim().toUpperCase() && (
                  <span
                    className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 font-bold pointer-events-none tabular-nums"
                    style={{ fontSize: `calc(${orcStyle.fieldSize} * 1px)` }}
                  >
                    {clienteCodigo
                      ? String(clienteCodigo).padStart(4, '0')
                      : String(clienteId).padStart(4, '0')}
                  </span>
                )}
                <input
                  type="text"
                  value={buscaCliente}
                  onChange={(e) => setBuscaCliente(upperMantendoCursor(e))}
                  onKeyDown={(e) => {
                    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && showClienteSugestoes && clientesSugestao.length > 0) {
                      tecladoCliente.onKeyDown(e);
                    } else if (e.key === 'Enter') {
                      if (showClienteSugestoes && clientesSugestao.length > 0 && tecladoCliente.highlight >= 0) {
                        tecladoCliente.onKeyDown(e);
                        buscaProdutoRef.current?.focus();
                      } else {
                        e.preventDefault();
                        confirmarCliente();
                        buscaProdutoRef.current?.focus();
                      }
                    }
                  }}
                  onBlur={() => { setTimeout(confirmarCliente, 150); }}
                  placeholder={clienteId === 1 && clienteNome === 'Consumidor' ? 'Consumidor' : ''}
                  disabled={bloqueadoEdicao}
                  className={`w-full pl-9 ${
                    clienteId !== 1 && buscaCliente.trim() === (clienteNome || '').trim().toUpperCase()
                      ? 'pr-[76px]'
                      : 'pr-8'
                  } py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-60 disabled:cursor-not-allowed`}
                  style={{
                    backgroundColor: orcStyle.fieldBg,
                    color: orcStyle.fieldFg,
                    fontSize: `calc(${orcStyle.fieldSize} * 1px)`,
                    fontWeight: orcStyle.fieldWeight as any,
                  }}
                />
                {(clienteId !== 1 || clienteNome !== 'Consumidor' || buscaCliente.trim() !== '') && !bloqueadoEdicao && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={limparCliente}
                    title="Tirar o cliente deste pedido (volta pra Consumidor)"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600 transition"
                  >
                    <X size={16} />
                  </button>
                )}
                {showClienteSugestoes && (clientesSugestao.length > 0 || contatosSugestao.length > 0) && (
                  // Lista mais larga que o campo, como a de Produto (pedido dele, 08/10/2026, com print
                  // marcando ~490px na janela dele); nunca menor que o proprio campo.
                  <div ref={tecladoCliente.listaRef} className="absolute z-20 top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto" style={{ width: 490, minWidth: '100%' }}>
                    {clientesSugestao.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500 border-b border-gray-100">Nenhum cliente cadastrado com esse nome</div>
                    )}
                    {clientesSugestao.map((c, idx) => (
                      <button
                        key={c.id}
                        data-idx={idx}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => tecladoCliente.setHighlight(idx)}
                        onClick={() => selecionarClienteSugestao(c)}
                        className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 ${idx === tecladoCliente.highlight ? 'bg-blue-50' : 'hover:bg-blue-50'}`}
                      >
                        <span className="text-gray-500 font-bold no-underline mr-2">
                          {c.codigo_legado
                            ? String(c.codigo_legado).padStart(4, '0')
                            : String(c.id).padStart(4, '0')}
                        </span>
                        {c.tipo_pessoa === 'PJ' && c.razao_social ? (
                          <>
                            <span className="font-medium">{c.razao_social}</span>
                            {c.nome && c.nome !== c.razao_social && (
                              <span className="text-gray-400 ml-2">({c.nome})</span>
                            )}
                          </>
                        ) : (
                          <span className="font-medium">{c.nome}</span>
                        )}
                        {c.cpf_cnpj && <span className="text-gray-400 ml-2">{c.cpf_cnpj}</span>}
                      </button>
                    ))}
                    {contatosSugestao.length > 0 && (
                      <>
                        {/* Cores fixas: a lista e' `bg-white` cravado, nao token de tema ([DA-678]). */}
                        <div className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide" style={{ backgroundColor: '#f3f4f6', color: '#374151' }}>
                          WhatsApp — ainda sem cadastro
                        </div>
                        {contatosSugestao.map((c) => (
                          <div
                            key={`ct-${c.contato_id}`}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm border-b border-gray-100"
                            style={{ color: '#111827' }}
                          >
                            <MessageCircle size={14} className="flex-shrink-0" style={{ color: '#16a34a' }} />
                            <span className="font-medium truncate flex-1 min-w-0" title={c.nome}>{c.nome}</span>
                            <span className="text-gray-500 tabular-nums flex-shrink-0">{mascaraTelefoneBR(c.numero)}</span>
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => void cadastrarContatoComoCliente(c)}
                              disabled={cadastrandoContato !== null}
                              className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-60"
                            >
                              {cadastrandoContato === c.contato_id ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />}
                              Cadastrar e usar
                            </button>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowModalListaClientes(true)}
                disabled={bloqueadoEdicao}
                className="px-2 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                title="Buscar / cadastrar cliente"
              >
                <UserPlus size={16} />
              </button>
            </div>
      </div>

      {bloqueadoEdicao && (
        <div
          className="flex items-center gap-2 mb-2 px-4 py-2 rounded-lg flex-shrink-0"
          style={{ backgroundColor: '#f97316', color: '#000' }}
        >
          <Lock size={16} />
          <span className="text-sm font-semibold flex-1">
            {edicaoDeOutro
              ? `Editado por ${edicaoDeOutro} — somente leitura. Quando ${edicaoDeOutro} sair do orcamento, atualize a tela (F5) para editar.`
              : ehAcerto
              ? 'Acerto de consignacao — gerado automaticamente da soma das remessas do cliente, somente leitura. Para finalizar, use o botao "Caixa". Para mudar o que sera cobrado, cancele o acerto, ajuste a devolucao nas remessas e refaca.'
              : ehVendaCm
              ? 'Venda CM liberada — estoque ja baixado. Somente leitura. A nota de venda sera emitida no fechamento do mes.'
              : remessaTravadaEdicao
              ? 'Esta remessa em consignacao (legada) ja foi emitida (NF-e autorizada) — travada para edicao. Abra pela aba "Vendas CM" para consultar; para corrigir, cancele a NF-e de remessa.'
              : levadoComCredito
              ? 'O cliente levou este orcamento pagando com credito da loja (Trocas e Devolucoes > Compra com Credito) — somente leitura. Para desfazer, cancele a compra no Historico de Trocas: o estoque e o credito voltam e o orcamento reabre.'
              : 'Este orcamento ja virou venda finalizada — travado para edicao. As alteracoes NAO sao salvas. Para corrigir a nota, reabra a venda no Caixa (cancelar/estornar) e refaca.'}
          </span>
          {ehVendaCm && savedId && (
            <button
              type="button"
              onClick={() => setModalDevolucaoVendaCm(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-sm text-white shrink-0"
              style={{ backgroundColor: '#12294f' }}
            >
              <Undo2 size={15} /> Registrar devolução
            </button>
          )}
        </div>
      )}

      <div className="flex flex-col gap-1 flex-1 min-h-0">
        {/* Consignacao (DA-371): banner do modo remessa — deixa claro que NAO e venda */}
        {tipoDoc === 'remessa' && !bloqueadoEdicao && (
          <div
            className="flex items-start gap-2 flex-shrink-0 px-4 py-2 rounded-lg"
            style={{ backgroundColor: '#fde68a', color: '#000' }}
          >
            <Package size={16} className="flex-shrink-0 mt-0.5" />
            <span className="text-sm font-medium">
              <b>Remessa em consignação.</b> O material sai com nota que ampara o transporte, mas{' '}
              <b>não é venda</b>: vai e volta, e você acerta só o que for usado numa venda no fim do mês.
              Não gera caixa, título nem comissão agora — o preço trava aqui, na saída.
            </span>
          </div>
        )}

        {/* Acerto: o banner do fechamento do mes agora e o de trava (laranja, no topo) — o acerto
            e' somente leitura (DA-373), entao nao ha mais banner amarelo editavel aqui. */}

        {/* Tabela de Itens */}
        <div ref={tabelaRef} className="bg-white rounded-xl shadow-sm border border-gray-200 flex-1 min-h-0 overflow-y-auto">
          <table className="w-full text-sm">
            <thead
              className="border-b border-gray-200 sticky top-0 z-10"
              style={{
                backgroundColor: orcStyle.headerBg,
                color: orcStyle.headerFg,
                fontSize: `calc(${orcStyle.headerSize} * 1px)`,
                fontWeight: orcStyle.headerWeight as any,
              }}
            >
              <tr>
                <th className="text-left px-3 py-2 font-medium w-8">#</th>
                <th className="w-6"></th>
                <th className="text-center px-1 py-2 font-medium w-12"></th>
                <th className="text-left px-3 py-2 font-medium">Codigo</th>
                <th className="text-left px-3 py-2 font-medium">Descricao</th>
                <th className="text-left px-3 py-2 font-medium w-28">Marca</th>
                <th className="text-center px-3 py-2 font-medium w-14">Un.</th>
                <th className="text-center px-3 py-2 font-medium w-20">Qtd</th>
                <th className="text-right px-3 py-2 font-medium w-28">Preco Unit.</th>
                <th className="text-right px-3 py-2 font-medium w-28">Subtotal</th>
                <th className="text-center px-2 py-2 font-medium w-16">Est.</th>
                <th className="text-center px-2 py-2 font-medium w-20">Local</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {itens.length === 0 ? (
                <tr>
                  <td colSpan={13} className="text-center py-8 text-gray-400">
                    Nenhum produto adicionado
                  </td>
                </tr>
              ) : (
                itens.map((item, idx) => {
                  const isDragging = draggingIdx === idx;
                  const isDragTarget = dragOverIdx === idx && draggingIdx !== null && draggingIdx !== idx;
                  const marcada = linhaMarcada === item.produto_id;
                  return (
                    <tr
                      key={idx}
                      ref={(el) => {
                        if (el) itemRefs.current[idx] = el;
                        else delete itemRefs.current[idx];
                      }}
                      draggable
                      onDragStart={(e) => {
                        setDraggingIdx(idx);
                        e.dataTransfer.effectAllowed = 'move';
                        try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* ignora */ }
                      }}
                      onDragEnter={(e) => {
                        if (draggingIdx !== null && draggingIdx !== idx) {
                          e.preventDefault();
                          setDragOverIdx(idx);
                        }
                      }}
                      onDragOver={(e) => {
                        if (draggingIdx !== null) {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (draggingIdx !== null && draggingIdx !== idx) dropItem(draggingIdx, idx);
                        setDraggingIdx(null);
                        setDragOverIdx(null);
                      }}
                      onDragEnd={() => {
                        setDraggingIdx(null);
                        setDragOverIdx(null);
                      }}
                      onMouseEnter={() => setHoverIdx(idx)}
                      onMouseLeave={() => setHoverIdx(null)}
                      onClick={() => setLinhaMarcada(item.produto_id)}
                      className={`border-b border-gray-100 ${isDragging ? 'opacity-40' : ''} ${
                        isDragTarget
                          ? draggingIdx! < idx
                            ? 'border-b-4 border-b-blue-500'
                            : 'border-t-4 border-t-blue-500'
                          : ''
                      }`}
                      style={{
                        // A marca do clique VENCE o hover: o hover e' passageiro (segue o mouse) e
                        // apagaria a linha marcada so de passar por cima dela.
                        backgroundColor: marcada
                          ? '#fde68a'
                          : hoverIdx === idx
                          ? orcStyle.selectBg
                          : idx % 2 === 0
                          ? orcStyle.rowBg
                          : orcStyle.rowAltBg,
                        color: marcada ? '#000' : hoverIdx === idx ? orcStyle.selectFg : orcStyle.rowFg,
                        fontSize: `calc(${orcStyle.rowSize} * 1px)`,
                        fontWeight: orcStyle.rowWeight as any,
                      }}
                    >
                      <td className="px-3 py-1 text-gray-400">{idx + 1}</td>
                      <td className="px-0 py-1">
                        <span className="text-gray-400 cursor-grab active:cursor-grabbing select-none" title="Arraste para reordenar">
                          <GripVertical size={14} />
                        </span>
                      </td>
                      <td className="px-1 py-0.5 text-center">
                        {item.foto_url ? (
                          <FotoProduto
                            url={item.foto_url}
                            tam={`calc(${orcStyle.imgSize} * 1px)`}
                            // verticalAlign: em linha de base a foto reservava ~8px embaixo
                            // (espaco das letras que descem) e cada produto subia pra 50px.
                            style={{ display: 'inline-block', verticalAlign: 'middle', cursor: 'zoom-in' }}
                            title="Clique para ampliar a foto"
                            onClick={() => setFotoAmpliada({ url: item.foto_url!, legenda: `${item.codigo_interno} — ${item.descricao}` })}
                          />
                        ) : (
                          <div
                            className="bg-gray-100 rounded border border-gray-200 inline-flex items-center justify-center"
                            style={{
                              width: `calc(${orcStyle.imgSize} * 1px)`,
                              height: `calc(${orcStyle.imgSize} * 1px)`,
                              verticalAlign: 'middle',
                            }}
                          >
                            <span className="text-gray-300 text-[8px]">Sem foto</span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-1 font-mono text-blue-600">{item.codigo_interno}</td>
                      <td className="px-3 py-1">{item.descricao}</td>
                      <td className="px-3 py-1">{item.marca_nome || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-1 text-center text-gray-500">{item.unidade_sigla || '-'}</td>
                      <td className="px-3 py-1">
                        <input
                          ref={(el) => { qtdRefs.current[idx] = el; }}
                          type="text"
                          inputMode="decimal"
                          value={qtdInputs[idx] ?? String(item.quantidade).replace('.', ',')}
                          onChange={(e) => onChangeQtdInput(idx, e.target.value)}
                          onBlur={() => onBlurQtdInput(idx)}
                          onKeyDown={handleEnterComoTab}
                          onFocus={(e) => e.target.select()}
                          disabled={bloqueadoEdicao}
                          className="w-full text-center border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                      </td>
                      <td
                        className="px-3 py-1"
                        title={item.reajustado ? 'Preco atualizado do cadastro do produto' : undefined}
                        // AMBAR, nao o amarelo #fde68a de antes: com a linha marcada tambem em
                        // amarelo (padrao de grid), o aviso de reajuste sumia dentro dela — o
                        // realce comia o sinal. O ambar sobrevive na linha marcada e fica ate mais
                        // legivel nas outras. Conferido injetando na tela real antes de codar.
                        style={item.reajustado ? { backgroundColor: AMARELO_REAJUSTE } : undefined}
                      >
                        <input
                          type="text"
                          inputMode="decimal"
                          value={precoInputs[idx] ?? Number(item.preco_unitario).toFixed(4).replace('.', ',')}
                          onChange={(e) => onChangePrecoInput(idx, e.target.value)}
                          onBlur={() => onBlurPrecoInput(idx)}
                          onKeyDown={handleEnterComoTab}
                          disabled={bloqueadoEdicao}
                          className="w-full text-right border border-gray-300 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                          style={item.reajustado ? { '--input-bg': AMARELO_REAJUSTE, '--input-fg': '#000' } as React.CSSProperties : undefined}
                        />
                      </td>
                      <td className="px-3 py-1 text-right font-medium whitespace-nowrap">R$ {fmt4(item.quantidade * Number(item.preco_unitario))}</td>
                      {/* Est. + Local orientam o vendedor ("tem?" e "onde esta?") — nao vao pro PDF
                          (gerarPdfOrcamento monta a propria lista de colunas). O acento so vale pro
                          estado normal: quando falta estoque, o EstoqueEfetivo pinta o vermelho por
                          dentro e ele vence, porque a classe do span manda no proprio span. */}
                      <td className="px-2 py-1 text-center font-semibold" style={{ color: 'var(--primary)' }}>
                        <EstoqueEfetivo
                          proprio={item.estoque_atual != null ? Number(item.estoque_atual) : null}
                          efetivo={item.estoque_efetivo != null ? Number(item.estoque_efetivo) : null}
                          necessario={item.quantidade}
                          compacto
                        />
                      </td>
                      <td
                        className="px-2 py-1 text-center whitespace-nowrap"
                        style={{
                          color: item.localizacao ? 'var(--primary)' : 'var(--text-muted)',
                          fontWeight: item.localizacao ? 600 : 400,
                        }}
                        title={item.localizacao ? `Localizacao fisica: ${item.localizacao}` : 'Produto sem localizacao cadastrada'}
                      >
                        {item.localizacao || '—'}
                      </td>
                      <td className="px-3 py-1">
                        <button
                          onClick={() => {
                            if (confirm(`Remover ${item.descricao}?`)) removerItem(idx);
                          }}
                          disabled={bloqueadoEdicao}
                          className="text-red-400 hover:text-red-600 transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-red-400"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Barra inferior */}
        <div className="flex items-stretch gap-2 flex-shrink-0">
          <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-2 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-1">
              <label className="text-[14px] font-medium text-amber-700 whitespace-nowrap">Retirado por:</label>
              <input
                type="text"
                value={comprador}
                onChange={(e) => setComprador(e.target.value)}
                disabled={bloqueadoEdicao}
                className="flex-1 min-w-0 px-2 py-0.5 border border-gray-300 rounded text-[14px] focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ backgroundColor: orcStyle.fieldBg, color: orcStyle.fieldFg }}
              />
              {/* Programa do Prestador (DA-435) — quem GANHA o credito. Fica aqui, ao lado do
                  "Retirado por", porque e' a faixa dos "quem" do pedido; e a lista so oferece quem
                  tem o selo. Vazio = venda comum, nao acumula nada. */}
              <label className="text-[14px] font-medium text-amber-700 whitespace-nowrap">Parceiro:</label>
              <div className="relative flex-1 min-w-0">
                {/* A lupa e' o unico sinal de que o campo BUSCA — sem ela ele se le como texto
                    livre, e a lista dos marcados fica invisivel (principio 1). */}
                <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={buscaPrestador}
                  onChange={(e) => { setBuscaPrestador(e.target.value); setPrestadorAberto(true); }}
                  onFocus={() => setPrestadorAberto(true)}
                  onClick={() => setPrestadorAberto(true)} // ja focado: clicar reabre depois de escolher
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') { setPrestadorAberto(false); return; }
                    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')
                      && showPrestadorSugestoes && prestadoresSugestao.length > 0) {
                      tecladoPrestador.onKeyDown(e);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setPrestadorAberto(false);
                      setShowPrestadorSugestoes(false);
                      // Campo APAGADO e' ordem de TIRAR o parceiro, nao texto pela metade. Antes o
                      // restaurador abaixo era incondicional e engolia o apagar: a vendedora limpava
                      // o campo, saia dele e o nome VOLTAVA sozinho — sem jeito de desmarcar a nao
                      // ser adivinhando o X.
                      if (!buscaPrestador.trim()) { limparPrestador(); return; }
                      // Texto pela metade, esse sim, nao pode ficar na tela parecendo preenchido:
                      // sem id nao ha parceiro, e o credito nasce do id. Volta pro que esta gravado.
                      setBuscaPrestador(prestadorNome);
                    }, 150);
                  }}
                  placeholder="BUSCAR PARCEIRO..."
                  disabled={bloqueadoEdicao}
                  className={`w-full pl-7 ${prestadorId ? 'pr-7' : 'pr-2'} py-0.5 border border-gray-300 rounded text-[14px] focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-60 disabled:cursor-not-allowed`}
                  style={{ backgroundColor: orcStyle.fieldBg, color: orcStyle.fieldFg }}
                />
                {prestadorId !== null && !bloqueadoEdicao && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={limparPrestador}
                    title="Tirar o parceiro deste pedido"
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-600 transition"
                  >
                    <X size={14} />
                  </button>
                )}
                {/* Abre pra CIMA: o campo mora na barra inferior, e um dropdown pra baixo cairia
                    fora da tela (mesma razao da regra de flip do CLAUDE.md). */}
                {showPrestadorSugestoes && prestadoresSugestao.length === 0 && (
                  // Lista vazia nao pode sumir calada: sem isto o campo abre o nada e parece
                  // quebrado. Diz TAMBEM onde se resolve — a adesao mora na ficha do cliente.
                  <div className="absolute z-20 bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-500">
                    {buscaPrestador.trim().length >= 2
                      ? 'Nenhum parceiro com esse nome.'
                      : 'Nenhum cliente marcado como Parceiro FIODECOBRE — o selo fica na ficha do cliente.'}
                  </div>
                )}
                {showPrestadorSugestoes && prestadoresSugestao.length > 0 && (
                  <div ref={tecladoPrestador.listaRef} className="absolute z-20 bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {prestadoresSugestao.map((c, idx) => (
                      <button
                        key={c.id}
                        data-idx={idx}
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => tecladoPrestador.setHighlight(idx)}
                        onClick={() => selecionarPrestador(c)}
                        className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 ${idx === tecladoPrestador.highlight ? 'bg-blue-50' : 'hover:bg-blue-50'}`}
                      >
                        <span className="text-gray-500 font-bold no-underline mr-2">
                          {c.codigo_legado
                            ? String(c.codigo_legado).padStart(4, '0')
                            : String(c.id).padStart(4, '0')}
                        </span>
                        <span className="font-medium">{c.nome}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Obs numa linha so, ao lado do rotulo (pedido dele, 02/10/2026: cada linha do rodape e'
                um produto a menos na lista). Cresce sozinha ate 3 linhas quando o texto e' longo
                (field-sizing), e dali rola — nada do que se digita fica escondido. */}
            <div className="flex items-start gap-2">
            <label className="text-[14px] font-medium text-gray-500 whitespace-nowrap" style={{ paddingTop: 2 }}>Obs:</label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={1}
              disabled={bloqueadoEdicao}
              className="flex-1 min-w-0 px-2 py-0.5 border border-gray-300 rounded resize-none focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                fieldSizing: 'content',
                maxHeight: '4.8em',
                overflowY: 'auto',
                backgroundColor: orcStyle.fieldBg,
                color: orcStyle.fieldFg,
                fontSize: `calc(${orcStyle.fieldSize} * 1px)`,
                fontWeight: orcStyle.fieldWeight as any,
              } as React.CSSProperties}
            />
            </div>
          </div>

          <div className="flex-shrink-0 bg-white rounded-xl shadow-sm border border-gray-200 p-2 flex flex-col justify-center gap-1 text-[14px]">
            <div className="flex items-center gap-2">
              <span className="text-gray-500 font-medium w-20 text-right whitespace-nowrap">Acrescimo</span>
              <button
                onClick={() => setAcrescimoMode('valor')}
                disabled={bloqueadoEdicao}
                className={`px-2 py-0.5 rounded text-[14px] font-medium border transition disabled:opacity-60 disabled:cursor-not-allowed ${
                  acrescimoMode === 'valor'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'
                }`}
              >
                R$
              </button>
              <button
                onClick={() => setAcrescimoMode('perc')}
                disabled={bloqueadoEdicao}
                className={`px-2 py-0.5 rounded text-[14px] font-medium border transition disabled:opacity-60 disabled:cursor-not-allowed ${
                  acrescimoMode === 'perc'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'
                }`}
              >
                %
              </button>
              <input
                type="number"
                value={acrescimoInput}
                onChange={(e) => {
                  if (e.target.value === '' && acrescimoInput !== '') acrescimoApagadoRef.current = true;
                  else if (e.target.value !== '') acrescimoApagadoRef.current = false;
                  setAcrescimoInput(e.target.value);
                }}
                onBlur={aplicarAcrescimo}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    aplicarAcrescimo();
                  }
                }}
                placeholder={acrescimoMode === 'valor' ? 'R$' : '%'}
                step={acrescimoMode === 'valor' ? '1' : '0.5'}
                min="0"
                disabled={bloqueadoEdicao}
                className="w-16 text-center border border-gray-300 rounded px-1 py-0.5 text-[14px] focus:ring-1 focus:ring-blue-500 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 font-medium w-20 text-right whitespace-nowrap">Desconto</span>
              <button
                onClick={() => setDescontoMode('valor')}
                disabled={bloqueadoEdicao}
                className={`px-2 py-0.5 rounded text-[14px] font-medium border transition disabled:opacity-60 disabled:cursor-not-allowed ${
                  descontoMode === 'valor'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'
                }`}
              >
                R$
              </button>
              <button
                onClick={() => setDescontoMode('perc')}
                disabled={bloqueadoEdicao}
                className={`px-2 py-0.5 rounded text-[14px] font-medium border transition disabled:opacity-60 disabled:cursor-not-allowed ${
                  descontoMode === 'perc'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'
                }`}
              >
                %
              </button>
              <input
                type="number"
                value={descontoInput}
                onChange={(e) => setDescontoInput(e.target.value)}
                onBlur={handleAplicarDesconto}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAplicarDesconto();
                  }
                }}
                placeholder={descontoMode === 'valor' ? 'R$' : '%'}
                step={descontoMode === 'valor' ? '1' : '0.5'}
                min="0"
                disabled={bloqueadoEdicao}
                className="w-16 text-center border border-gray-300 rounded px-1 py-0.5 text-[14px] focus:ring-1 focus:ring-blue-500 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>
            {descontoTotalValor > 0.005 && (
              <div
                className={`text-[14px] px-2 py-0.5 rounded text-center ${
                  descontoAcimaDoLimite()
                    ? 'bg-red-50 text-red-600'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Desc. acumulado: {fmt(calcDescontoAcumulado())}% (R$ {fmt(descontoTotalValor)})
                {/* "Requer senha" so quando a senha e' MESMO exigida — mesma condicao da porta do
                    Enviar ao Caixa (DA-405). Antes o selo ignorava o admin e o carimbo ja gravado,
                    entao anunciava uma trava que nao ia acontecer. */}
                {descontoAcimaDoLimite() && !isAdmin && !senhaGerencialUsada && !senhaGerencialPersistida && (
                  <span className="ml-1 font-medium">· Requer senha</span>
                )}
                {senhaGerencialUsada && <span className="ml-1 text-green-600">· Autorizado</span>}
              </div>
            )}
            {overrideBase != null && (
              <div
                className="text-[14px] px-2 py-1 rounded text-center font-semibold"
                style={{ backgroundColor: '#fde68a', color: '#000' }}
                title="Base manual aplicada (Ctrl+Shift+X). Qualquer edicao de item/preco/desconto remove."
              >
                ⚠ Base manual: R$ {fmt(overrideBase)}
                {overrideAuto != null && (
                  <span className="ml-1 font-normal">· sugerido R$ {fmt(overrideAuto)}</span>
                )}
              </div>
            )}
          </div>

          <div
            className={`flex-shrink-0 rounded-xl shadow-sm flex flex-col items-end justify-center px-6 py-2 min-w-[200px] select-none ${
              bloqueadoEdicao ? 'cursor-default' : 'cursor-pointer'
            }`}
            style={{ backgroundColor: orcStyle.totalBg }}
            onClick={() => !editandoTotal && handleTotalClick()}
            // Sem a troca do cursor a caixa continuaria CONVIDANDO ao clique num documento travado
            // (a mao do mouse e' a promessa). O handleTotalClick ja recusa; isto e' a promessa honesta.
            title={
              bloqueadoEdicao
                ? undefined
                : 'Clique para definir o valor final (acrescimo livre; desconto acima de 5% pede senha gerencial)'
            }
          >
            {editandoTotal ? (
              <div className="flex items-center gap-1">
                <span style={{ color: orcStyle.totalFg, fontSize: `calc(${orcStyle.totalSize} * 0.8px)`, fontWeight: orcStyle.totalWeight as any }}>R$</span>
                <input
                  ref={totalInputRef}
                  type="text"
                  inputMode="decimal"
                  value={totalOverrideInput}
                  onChange={(e) => setTotalOverrideInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmarTotalOverride();
                    if (e.key === 'Escape') setEditandoTotal(false);
                  }}
                  onBlur={confirmarTotalOverride}
                  placeholder={fmt(total)}
                  className="w-32 text-center border-2 border-white/50 rounded-lg bg-white/20 outline-none"
                  style={{
                    color: orcStyle.totalFg,
                    fontSize: `calc(${orcStyle.totalSize} * 0.8px)`,
                    fontWeight: orcStyle.totalWeight as any,
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ) : (
              <div className="w-full">
                {/* lineHeight 1.2 (era o 1.5 herdado): mesmas letras, menos vao entre as tres
                    linhas — o quadro do total e' quem dita a altura do rodape. */}
                <table style={{ color: 'var(--orc-total-fg)', fontSize: 20, lineHeight: 1.2, borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr style={{ opacity: 0.75 }}>
                      <td style={{ textAlign: 'left', paddingRight: 40, whiteSpace: 'nowrap' }}>Bruto</td>
                      <td className="font-semibold tabular-nums" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        R$ {fmt(totalOriginal)}
                      </td>
                    </tr>
                    <tr style={{ opacity: 0.75 }}>
                      <td style={{ textAlign: 'left', paddingRight: 40, whiteSpace: 'nowrap' }}>Desconto</td>
                      <td className="font-semibold tabular-nums" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        R$ {fmt(descontoTotalValor)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: 'left', paddingRight: 40, fontSize: 21, fontWeight: orcStyle.totalWeight as any, whiteSpace: 'nowrap' }}>
                        Liquido
                      </td>
                      <td
                        className="tabular-nums"
                        style={{
                          textAlign: 'right',
                          fontSize: `calc(${orcStyle.totalSize} * 0.65px)`,
                          fontWeight: orcStyle.totalWeight as any,
                          whiteSpace: 'nowrap',
                          color: orcStyle.totalFg,
                        }}
                      >
                        R$ {fmt(total)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modais */}
      {/* O modal "Venda CM — Liberar sem NF-e" saiu daqui na DA-415 (foi pro ModalFinalizarVenda,
          no caixa). O de DEVOLUCAO fica: a devolucao do material continua sendo desta tela. */}

      {modalDevolucaoVendaCm && savedId && (
        <ModalRegistrarDevolucaoVendaCm
          orcamentoId={savedId}
          onClose={() => setModalDevolucaoVendaCm(false)}
          onSucesso={() => {
            setModalDevolucaoVendaCm(false);
            toast.sucesso('Devolução registrada — estoque estornado.');
            // Igual a liberacao (confirmarVendaCm): o saldo mudou, entao volta pra aba "Vendas CM"
            // pra o operador ver o pedido pousar la com o status novo (parcial/devolvida), em vez de
            // ficar parado no doc read-only sem sinal do que mudou.
            navigate('/orcamentos', { state: { aba: 'remessas' } });
          }}
        />
      )}

      {showModalSenha && (
        <ModalSenhaGerencial onConfirm={handleSenhaAprovada} onCancel={() => setShowModalSenha(false)} />
      )}

      {showModalOverride && (
        <ModalOverrideBase
          autoSugerido={totalOriginal}
          onAplicar={(v) => {
            setShowModalOverride(false);
            aplicarOverrideBase(v);
          }}
          onCancel={() => setShowModalOverride(false)}
        />
      )}

      {showModalWhatsApp && (
        <ModalEnviarWhatsApp
          orcamentoId={savedId}
          numero={orcamentoNumero ?? undefined}
          clienteId={clienteId}
          clienteNome={clienteNome}
          {...(flyPronto ? {
            imagensProntas: [flyPronto.imagem_base64],
            filenamePronto: flyPronto.filename,
            captionPersonalizado: flyPronto.legenda,
            fly: true,
          } : {})}
          obterPdfBase64={async () => {
            const doc = await gerarPdfOrcamento(montarDadosPdf());
            const dataUri = doc.output('datauristring');
            const base64 = dataUri.split('base64,')[1] ?? '';
            return { base64, filename: `${orcamentoNumero || 'orcamento'}.pdf` };
          }}
          onClose={() => { setShowModalWhatsApp(false); setFlyPronto(null); }}
        />
      )}

      {showModalEnviar && (
        <ModalEnviarCaixa
          orcamentoId={savedId}
          clienteId={clienteId}
          clienteNome={clienteNomeEfetivo}
          numeroOrcamento={orcamentoNumero}
          comprador={comprador}
          itensOrcamento={itens.map((it, idx) => ({
            codigo: it.codigo_interno,
            descricao: it.descricao,
            quantidade: it.quantidade,
            unidade: it.unidade_sigla,
            preco_unitario: it.preco_unitario,
            subtotal: subtotaisExibidos[idx] ?? it.subtotal,
          }))}
          formasPagamento={formasPagamento}
          formaPagamentoId={formaPagamentoId}
          totalOrcamento={total}
          totalSemDesconto={totalOriginal}
          temDescontos={calcDescontoAcumulado() > 0}
          enviando={enviando}
          onConfirm={confirmarEnviarCaixa}
          onCancel={() => setShowModalEnviar(false)}
          telefoneRecusado={telefoneRecusado}
          contatoCapturadoId={contatoCapturadoId}
          onEscolherCliente={(c) => {
            // Mesmo gesto do seletor da tela (showModalListaClientes): quem grava e' o autosave
            // daqui, e o envio espera por ele (`gravarPendencias`).
            setClienteId(c.id);
            setClienteNome(c.nome);
            setClienteCodigo(c.codigo_legado || null);
            aplicarSeloConsignacao(c.consignacao === true);
            setBuscaCliente(c.nome);
            setShowClienteSugestoes(false);
            setNfeValidacao({ valido: true, faltam: [] });
            if (tipoNF === 'nfe') selecionarTipoNF('nfe', c.id);
          }}
          onRemoverCliente={limparCliente}
          onCapturouContato={(contatoId) => setContatoCapturadoId(contatoId)}
          recusaNome={recusaNome}
          recusaEtiquetaIds={recusaEtiquetaIds}
          onRecusarTelefone={(nome, ids) => {
            setTelefoneRecusado(true);
            setRecusaNome(nome);
            setRecusaEtiquetaIds(ids);
          }}
        />
      )}

      {showModalVendaMinima && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div
            className="bg-white rounded-lg shadow-xl px-8 py-6 max-w-sm text-center"
            style={{ position: 'relative' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="btn-fechar-modal btn-fechar-modal--abs" onClick={() => setShowModalVendaMinima(false)} aria-label="Fechar">×</button>
            <div className="text-red-600 text-lg font-bold mb-4">VENDA MÍNIMA PERMITIDA R$ 1,00</div>
            <button
              onClick={() => setShowModalVendaMinima(false)}
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-sm font-medium"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {showModalListaClientes && (
        <ModalBuscarCliente
          onSelect={(c) => {
            setClienteId(c.id);
            setClienteNome(c.nome);
            setClienteCodigo(c.codigo_legado || null);
            aplicarSeloConsignacao(c.consignacao === true);
            setBuscaCliente(c.nome);
            setShowClienteSugestoes(false);
            setNfeValidacao({ valido: true, faltam: [] });
            if (tipoNF === 'nfe') selecionarTipoNF('nfe', c.id);
            setShowModalListaClientes(false);
          }}
          onClose={() => setShowModalListaClientes(false)}
        />
      )}

      {fotoAmpliada && (
        <LightboxImagem
          imagens={[{ url: fotoAmpliada.url, legenda: fotoAmpliada.legenda }]}
          onFechar={() => setFotoAmpliada(null)}
        />
      )}
    </div>
  );
}
